import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api } from '../services/api';
import EmailPicker, { looksLikeEmail } from '../components/EmailPicker';
import CustomerView from '../components/CustomerView';
import CustomerIntake from '../components/CustomerIntake';
import BidPlanPicker from '../components/BidPlanPicker';
import BidStartDays from '../components/BidStartDays';
import BushSizeEditor, { SizeCatalogItem, SizeOption } from '../components/BushSizeEditor';
import { useAuth } from '../services/AuthContext';
import { businessToday, fmtTs, parseDbTs } from '../utils/time';
import { officeLink } from '../utils/bidOpened';
import { shrinkPhoto } from '../utils/image';
import {
  Plus, X, Edit2, Trash2, Check,
  ToggleLeft, ToggleRight, Camera, Share2,
  Copy, ExternalLink, UploadCloud, Mail, MessageSquare, RefreshCw, Eye, FileText, Search, Snowflake, Leaf, Map as MapIcon } from 'lucide-react';
import { toast } from 'react-toastify';
import VisitHours from '../components/VisitHours';
import BidScopeEditor from '../components/BidScopeEditor';
import BidOptionBuilder, { BuilderOption } from '../components/BidOptionBuilder';
import ChangeOrderEditor from '../components/ChangeOrderEditor';
import ChangeOrderHistory from '../components/ChangeOrderHistory';
import { useReadOnly } from '../hooks/useReadOnly';
import AccountMark from '../components/AccountMark';

const STATUSES = ['draft', 'sent', 'accepted', 'declined', 'expired', 'completed', 'converted'] as const;

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-600',
  sent: 'bg-blue-100 text-blue-700',
  accepted: 'bg-green-100 text-green-700',
  declined: 'bg-red-100 text-red-600',
  expired: 'bg-yellow-100 text-yellow-700',
  completed: 'bg-brand-100 text-brand-700',
  converted: 'bg-teal-100 text-teal-700',
};

/**
 * "Opened 3 times · last Tue 7:40 PM" or "Not opened yet" — the customer's own opens of a sent bid, counted by the
 * bid page (server/services/bidOpened; owner, Oct 20 2026: "give me a notification if client opens the bid").
 * The office's Preview and his own phone are never counted.
 */
type WinterDeal = { on: boolean; pct: number; offerable: boolean; can_offer: number[]; offered_ids?: number[]; bid_on?: boolean; default_on?: boolean };
// The plant sizes a line may be offered in (server: services/bushSizes): the catalog the picker ticks from, which
// lines could carry sizes, and what each line offers today.
type SizesDeal = {
  catalog: SizeCatalogItem[];
  can_offer: number[];
  offered: Record<string, { options: SizeOption[]; chosen: string | null }>;
};
// The care plan this bid offers after a planting (server: services/bushFollowUp): the prices he typed for every
// 3 / 4 / 6 months, whether the bid reads like a planting job, and whether the automatic care note is switched on.
type BushPlan = {
  on: boolean; months: number[];
  prices: Record<string, number | null>;
  offered: { months: number; price: number; words: string; per_year: number }[];
  looks_like_planting: boolean;
};

function openedWords(q: any): string | null {
  const n = Number(q?.view_count) || 0;
  if (n > 0) {
    const last = parseDbTs(q.last_viewed_at);
    const recent = last && Date.now() - last.getTime() < 6 * 86400000;
    const when = fmtTs(q.last_viewed_at, recent ? { weekday: 'short', hour: 'numeric', minute: '2-digit' } : { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    return `Opened ${n === 1 ? 'once' : `${n} times`} · last ${when}`;
  }
  // Only a bid sent since opens were counted can say it has not been opened (the server's opened_tracked).
  return q?.status === 'sent' && q?.opened_tracked ? 'Not opened yet' : null;
}

/** A project a job cannot take the place of — why, in a few words (the keys come from services/quoteConvert). */
function projectOwnWords(own: string): string {
  if (own === 'bill') return 'has a bill';
  if (own === 'people') return 'has people on it — take them off there to make the job instead';
  if (own === 'photos') return 'has photos on it';
  if (own === 'price') return 'has its own lines or price';
  return `is marked ${String(own || '').replace(/^status:/, '').replace(/_/g, ' ')}`;
}

const SERVICE_TYPES = [
  { value: 'mowing', label: 'Mowing' },
  { value: 'landscaping', label: 'Landscaping' },
  { value: 'other', label: 'Other' },
];

function fmtMoney(n?: number | null) {
  if (n == null || n === 0) return '$0.00';
  return `$${Number(n).toFixed(2)}`;
}

/**
 * Subcontracted work (owner, Sep 30 2026: "it should just be line items —
 * make that a feature, I only do that for subcontracted work, and allow me to
 * change the percentage"). A line is plain unless it is marked subcontracted;
 * then its price is the sub's cost plus his markup. The customer only ever
 * sees the price. The last markup he used is the next one's starting point.
 */
function markedUp(cost: string, pct: string) {
  const c = Number(cost), p = Number(pct);
  if (!Number.isFinite(c) || c <= 0 || !Number.isFinite(p) || p < 0) return '';
  return (Math.round(c * (1 + p / 100) * 100) / 100).toFixed(2);
}
const MARKUP_KEY = 'kam-sub-markup-pct';
function lastMarkup() { try { return localStorage.getItem(MARKUP_KEY) || '5'; } catch { return '5'; } }
function rememberMarkup(p: string) { try { if (p !== '' && Number(p) >= 0) localStorage.setItem(MARKUP_KEY, p); } catch { /* no storage */ } }
// A line billed per month on its own rhythm (server: services/monthlyLine — owner, Oct 26 2026: "bush trimming for
// $100/month, quarterly"): priced per month, done every N weeks / months / mows, with an optional first day.
type EveryUnit = 'weeks' | 'months' | 'mows';
type NewLine = { description: string; quantity: string; price: string; sub: boolean; cost: string; pct: string; unit: '' | 'month' | 'visit'; every_n: string; every_unit: EveryUnit; first_on: string };
const blankLine = (): NewLine => ({ description: '', quantity: '1', price: '', sub: false, cost: '', pct: '', unit: '', every_n: '', every_unit: 'months', first_on: '' });
/** "every 3 months" · "every week" · "every other mow" — the server's words (monthlyLine.rhythmWords), for the office list. */
export function rhythmWords(n: number | string | null | undefined, unit: string | null | undefined): string {
  const k = Math.round(Number(n)); const u = String(unit || '');
  if (!(k >= 1) || !['weeks', 'months', 'mows'].includes(u)) return '';
  if (u === 'mows') return k === 1 ? 'every mow' : k === 2 ? 'every other mow' : `every ${k}${k === 3 ? 'rd' : 'th'} mow`;
  if (u === 'months' && k === 12) return 'once a year';
  if (u === 'months' && k === 6) return 'twice a year';
  const one = u === 'weeks' ? 'week' : 'month';
  return k === 1 ? `every ${one}` : `every ${k} ${one}s`;
}
/** How often a line billed per month is done — asked whenever "per month" is picked, so no $/month line is left without one. */
function RhythmFields({ n, unit, firstOn, onChange }: { n: string; unit: EveryUnit; firstOn: string; onChange: (patch: { every_n?: string; every_unit?: EveryUnit; first_on?: string }) => void }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-700">
      <span className="font-medium">Done every</span>
      <input type="number" inputMode="numeric" min="1" max="52" value={n} onChange={e => onChange({ every_n: e.target.value })} className="input text-sm w-16" placeholder="3" title="How many" />
      <select value={unit} onChange={e => onChange({ every_unit: e.target.value as EveryUnit })} className="input text-sm w-28" title="Weeks, months, or mows">
        <option value="weeks">week(s)</option><option value="months">month(s)</option><option value="mows">mow(s) — rides the mow</option>
      </select>
      <span className="text-gray-500">first on</span>
      <input type="date" value={firstOn} onChange={e => onChange({ first_on: e.target.value })} className="input text-sm w-40" title="Optional — else the bid's first visit" />
      {n && rhythmWords(n, unit) && <span className="text-gray-500">→ {rhythmWords(n, unit)}{unit === 'months' && Number(n) > 0 ? `, ${Math.round(120 / Number(n)) / 10} visits a year` : ''}</span>}
    </div>
  );
}

/** The sub's cost + markup under a line, for the office. */
function SubFields({ cost, pct, onChange }: { cost: string; pct: string; onChange: (cost: string, pct: string) => void }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
      <span>Sub's cost</span>
      <div className="relative">
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
        <input type="number" inputMode="decimal" min="0" step="0.01" value={cost} onChange={e => onChange(e.target.value, pct)}
          className="input text-sm pl-5 w-28" placeholder="0" aria-label="Sub's cost" />
      </div>
      <span>+</span>
      <div className="relative">
        <input type="number" inputMode="decimal" min="0" step="0.5" value={pct} onChange={e => onChange(cost, e.target.value)}
          className="input text-sm pr-6 w-20" aria-label="Markup percent" />
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-sm">%</span>
      </div>
      <span className="text-gray-500">{markedUp(cost, pct) ? `= $${markedUp(cost, pct)} to the customer` : 'markup'}</span>
    </div>
  );
}

export default function Quotes({ openQuoteId = null }: { openQuoteId?: number | null } = {}) {
  const qc = useQueryClient();
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  const [sheetsExporting, setSheetsExporting] = useState(false);
  const [sheetsMsg, setSheetsMsg] = useState<string | null>(null);
  const [modal, setModal] = useState<'new' | 'detail' | null>(null);
  const [selectedQuote, setSelectedQuote] = useState<any | null>(null);
  const [newForm, setNewForm] = useState({ customer_id: '', property_id: '', title: '', intro_note: '', notes: '', referral_code: '' });
  const [customerSearch, setCustomerSearch] = useState('');
  // A 'bidder' login can build quotes but can't reach the full customer/property
  // endpoints (balances, internal fields), so it reads name/address-only pickers.
  const { user } = useAuth();
  const isBidder = user?.role === 'bidder';
  // On-site bidding: type the client in right there, then quote them.
  const emptyClient = { name: '', phone: '', email: '', address: '', city: '', zip_code: '' };
  const [newClient, setNewClient] = useState<typeof emptyClient | null>(null);
  // Arriving from a job visit with "New quote for this customer" pre-selects them.
  const location = useLocation();
  const navigate = useNavigate();
  React.useEffect(() => {
    const pre = (location.state as any)?.prefillCustomerId;
    const prop = (location.state as any)?.prefillPropertyId;
    if (pre) {
      setNewForm(f => ({ ...f, customer_id: String(pre), property_id: prop ? String(prop) : f.property_id }));
      setModal('new');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const { data: quotesData, isLoading } = useQuery(['quotes', filterStatus], () =>
    api.get('/quotes', { params: filterStatus ? { status: filterStatus } : {} }).then(r => r.data.quotes as any[])
  );
  // Arriving from a customer's profile with a quote picked: open it.
  const openedRef = React.useRef<number | null>(null);
  React.useEffect(() => {
    // …or from a link: /quotes?open=123, or /quotes?find=Screens and concrete (the newest bid with that title).
    const sp = new URLSearchParams(location.search);
    const byTitle = sp.get('find') && quotesData
      ? [...quotesData].filter((x: any) => String(x.title || '').toLowerCase() === String(sp.get('find')).toLowerCase()
          && (!sp.get('customer') || String(x.customer_name || '').toLowerCase().includes(String(sp.get('customer')).toLowerCase())))
          .sort((a: any, b: any) => Number(b.id) - Number(a.id))[0]?.id
      : null;
    // ?id= opens a bid too: Needs Attention and the inbox have linked bids that way for a long time, and landed on the list.
    const oq = openQuoteId ?? (location.state as any)?.openQuoteId ?? (Number(sp.get('open')) || Number(sp.get('id')) || byTitle || null);
    if (!oq || !quotesData || openedRef.current === oq) return;
    const q = quotesData.find((x: any) => x.id === oq);
    if (q) { openedRef.current = oq; setSelectedQuote(q); setModal('detail'); window.history.replaceState({}, ''); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, location.search, quotesData, openQuoteId]);
  const { data: custData } = useQuery(['customers-list', isBidder], () =>
    isBidder
      ? api.get('/quotes/pickers/customers').then(r => r.data as any[])
      : api.get('/customers').then(r => r.data.customers as any[])
  );
  const { data: propData } = useQuery(
    ['props-for-quote', newForm.customer_id, isBidder],
    () => api.get(isBidder ? '/quotes/pickers/properties' : '/properties', { params: { customer_id: newForm.customer_id } }).then(r => r.data.properties as any[]),
    { enabled: !!newForm.customer_id }
  );
  const { data: smsStatus } = useQuery('sms-status',
    () => api.get('/integrations/sms-status').then(r => r.data as { configured: boolean }),
    { retry: false }
  );

  const { data: detailData, refetch: refetchDetail } = useQuery(
    ['quote-detail', selectedQuote?.id],
    () => api.get(`/quotes/${selectedQuote!.id}`).then(r => r.data),
    { enabled: modal === 'detail' && !!selectedQuote }
  );
  const { data: catalogData } = useQuery('service-catalog', () =>
    api.get('/service-catalog').then(r => r.data.services as any[])
  );
  // Convert-to-job data is admin-only; a bidder can't convert, so skip these
  // (they'd 403 for a bidder anyway).
  const { data: routesData } = useQuery('routes-for-convert', () =>
    api.get('/routes').then(r => r.data.routes as any[]), { enabled: !isBidder }
  );
  const { data: crewsData } = useQuery('crews-for-convert', () =>
    api.get('/crews').then(r => r.data.crews as any[]), { enabled: !isBidder }
  );
  const { data: membersData } = useQuery('members-for-convert', () =>
    api.get('/crews/members/all').then(r => r.data.members as any[]), { enabled: !isBidder }
  );

  const [aiText, setAiText] = useState('');
  const aiDraft = useMutation(async () => {
    const res = await api.post('/quotes/ai-draft', {
      text: aiText, customer_id: Number(newForm.customer_id), property_id: newForm.property_id ? Number(newForm.property_id) : null,
    });
    return res.data;
  }, {
    onSuccess: (data: any) => {
      qc.invalidateQueries('quotes');
      toast.success(data?.message || 'Drafted');
      setModal(null); setAiText('');
      setNewForm({ customer_id: '', property_id: '', title: '', intro_note: '', notes: '', referral_code: '' });
      setCustomerSearch('');
      setSelectedQuote(data.quote);
      setModal('detail');
    },
    onError: (e: any) => { toast.error(e.response?.data?.message || e.message || 'Could not draft that'); },
  });

  // Line items typed on the New Bid form: what it is, how many, price each.
  const [newLines, setNewLines] = useState<NewLine[]>([blankLine()]);
  // Owner, Sep 19 2026: "it wouldn't let me create a mowing bid because all it's got is line item bids… per visit."
  // The New Bid form makes either kind; mowing comes first because it is what he bids most.
  const [bidKind, setBidKind] = useState<'mowing' | 'lines'>('mowing');
  // "Offer lawn plans on this bid" (owner, Oct 22 2026: "some bids don't need to offer it"): starts from the default in
  // Settings → Bids, flipped here per bid before it is sent; the bid itself has the same switch.
  const { data: bidDefaults } = useQuery('bid-defaults', () => api.get('/quotes/bid-defaults').then(r => r.data as { offer_plans: number }), { staleTime: 60_000 });
  const [offerPlans, setOfferPlans] = useState<boolean | null>(null);   // null: not touched — the default
  const offerPlansOn = offerPlans ?? (bidDefaults ? Number(bidDefaults.offer_plans) !== 0 : true);
  const emptyMow = { frequency: 'weekly', price: '', overgrowth: '', one_time_fee: '', start_date: '', start_time: '' };
  const [mow, setMow] = useState(emptyMow);
  const createMowing = useMutation(async () => {
    if (!newForm.customer_id) throw new Error('Pick the customer');
    if (!newForm.property_id) throw new Error('Pick the yard this bid is for');
    const res = await api.post('/quotes/mowing', {
      customer_id: Number(newForm.customer_id), property_id: Number(newForm.property_id), frequency: mow.frequency,
      price_per_mow: mow.price === '' ? undefined : Number(mow.price), overgrowth_fee: mow.overgrowth === '' ? undefined : Number(mow.overgrowth), one_time_fee: mow.one_time_fee === '' ? undefined : Number(mow.one_time_fee),
      start_date: mow.start_date || undefined, start_time: mow.start_time || undefined, notes: newForm.notes || undefined, referral_code: newForm.referral_code || undefined,
      offer_plans: offerPlansOn ? 1 : 0,
    });
    return res.data;
  }, {
    onSuccess: (d: any) => {
      qc.invalidateQueries('quotes');
      toast.success(d.message || 'Mowing bid drafted');
      setModal(null);
      setNewForm({ customer_id: '', property_id: '', title: '', intro_note: '', notes: '', referral_code: '' });
      setMow(emptyMow);
      setOfferPlans(null);
      setCustomerSearch('');
      setSelectedQuote(d.quote);
      setModal('detail');
    },
    onError: (e: any) => { toast.error(e.response?.data?.message || e.message || 'Could not draft the mowing bid'); },
  });
  const createQuote = useMutation(async () => {
    if (!newForm.customer_id || !newForm.title.trim()) throw new Error('Customer and title required');
    // A line billed per month must say how often it is done (server: services/monthlyLine) — never a silent "every 4 weeks".
    const noRhythm = newLines.find(l => l.description.trim() && Number(l.price) > 0 && l.unit === 'month' && !(Number(l.every_n) >= 1));
    if (noRhythm) throw new Error(`How often is "${noRhythm.description.trim()}" done? Pick every N weeks, months or mows.`);
    const res = await api.post('/quotes', {
      ...newForm,
      customer_id: Number(newForm.customer_id),
      property_id: newForm.property_id ? Number(newForm.property_id) : null,
      offer_plans: offerPlansOn ? 1 : 0,
    });
    const q = res.data.quote;
    const rows = newLines.map(l => ({ description: l.description.trim(), quantity: Math.max(1, Number(l.quantity) || 1), price: Number(l.price),
      sub_cost: l.sub && Number(l.cost) > 0 ? Number(l.cost) : null, markup_pct: l.sub ? Number(l.pct) || 0 : null,
      unit: l.unit || null, every_n: l.unit === 'month' ? Number(l.every_n) || null : null, every_unit: l.unit === 'month' ? l.every_unit : null, first_on: l.unit === 'month' && l.first_on ? l.first_on : null }))
      .filter(l => l.description && Number.isFinite(l.price) && l.price > 0);
    for (let i = 0; i < rows.length; i++) {
      const l = rows[i];
      await api.post(`/quotes/${q.id}/line-items`, { description: l.description, quantity: l.quantity, unit_price: l.price, is_optional: 0, is_selected: 1, sort_order: i, sub_cost: l.sub_cost, markup_pct: l.markup_pct,
        unit: l.unit, every_n: l.every_n, every_unit: l.every_unit, first_on: l.first_on });
    }
    return q;
  }, {
    onSuccess: (quote) => {
      qc.invalidateQueries('quotes');
      toast.success('Bid created');
      setModal(null);
      setNewForm({ customer_id: '', property_id: '', title: '', intro_note: '', notes: '', referral_code: '' });
      setNewLines([blankLine()]);
      setOfferPlans(null);
      setCustomerSearch('');
      setSelectedQuote(quote);
      setModal('detail');
    },
    onError: (e: any) => { toast.error(e.response?.data?.message || e.message || 'Failed to create'); },
  });

  const updateStatus = useMutation(async ({ id, status }: { id: number; status: string }) => {
    // Accepting by hand does what the customer's tap does: a mowing bid goes on the schedule.
    if (status === 'accepted') { const r = await api.post(`/quotes/${id}/accept-office`, {}); return r.data; }
    const r = await api.put(`/quotes/${id}`, { status });
    // Mark Declined on a bid with an open change order takes it back first, and says so.
    return r.data?.message ? { message: r.data.message } : null;
  }, {
    onSuccess: (d: any) => { qc.invalidateQueries('quotes'); qc.invalidateQueries('jobs'); refetchDetail(); toast.success(d?.message || 'Status updated', { autoClose: 8000 }); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Failed to update status'); },
  });

  const deleteQuote = useMutation(async (id: number) => api.delete(`/quotes/${id}`), {
    onSuccess: (r: any) => {
      qc.invalidateQueries('quotes');
      setModal(null);
      setSelectedQuote(null);
      // A manager's delete is captured for the owner (202) — the card stays
      // until it is approved, so don't claim it is gone.
      if (r?.status !== 202) toast.success('Quote deleted');
    },
    // A bid with a paid deposit or change orders on file is kept — in the server's words.
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Failed to delete'); },
  });

  // The search box: name, company, address, title, phone or bid number — every word has to match somewhere.
  const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const digitsOf = (v: any) => String(v || '').replace(/\D/g, '');
  const quotes = (quotesData || []).filter((q: any) => {
    if (!words.length) return true;
    const hay = [q.customer_name, q.company_name, q.title, q.address, q.city, q.property_label, q.status, `#${q.id}`].map(v => String(v || '').toLowerCase()).join(' ');
    return words.every(w => hay.includes(w) || (/^\d{3,}$/.test(w) && digitsOf(q.customer_phone).includes(w)) || (/^#?\d+$/.test(w) && String(q.id) === w.replace('#', '')));
  });
  const customers = custData || [];
  const properties = propData || [];
  const catalog = catalogData || [];
  const routes = routesData || [];
  const crews = crewsData || [];
  const members = membersData || [];
  const quote = detailData?.quote || selectedQuote;
  const lineItems = detailData?.lineItems || [];
  // Several options on one bid (server: services/bidOptions) — [] on a bid with none, which is every bid until today.
  const bidOptions = detailData?.bid_options || [];
  const groups = detailData?.groups || [];
  const media = detailData?.media || [];
  const schedule = detailData?.schedule || null;
  const oneTimeMow = detailData?.one_time_mow || null;   // the one mow an accepted one-time bid booked
  const converted_job = detailData?.converted_job || null;
  const converted_project = detailData?.converted_project || null;   // the bid became Project Work and has no job yet
  const depositInfo = detailData?.deposit || null;
  const checklist = detailData?.checklist || [];
  const changeOrders = detailData?.change_orders || null;   // the open change order and the history lines (accepted bids)
  // "Do it in winter, save 10%" (services/winterWork): the deal as set, whether this bid can carry it, and which lines could.
  const winterDeal: WinterDeal | null = detailData?.winter || null;
  // The plant sizes a planting line can be offered in (services/bushSizes): the catalog, and each line's own sizes.
  const sizesDeal: SizesDeal | null = detailData?.sizes || null;
  // The care plan after a planting (services/bushFollowUp): its three prices, as the bid carries them.
  const bushPlan: BushPlan | null = detailData?.bush_plan || null;

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-gray-900">Bids</h1>
        <div className="flex items-center gap-2">
          {!isBidder && (
            <button onClick={() => navigate('/bids')} className="btn-secondary text-xs">Quote visits</button>
          )}
          {!isBidder && (
          <button
            onClick={async () => {
              setSheetsExporting(true);
              setSheetsMsg(null);
              try {
                const res = await api.post('/integrations/sheets/export/quotes');
                setSheetsMsg(res.data.message);
              } catch (err: any) {
                setSheetsMsg(err.response?.data?.message || 'Export failed');
              } finally {
                setSheetsExporting(false);
              }
            }}
            disabled={sheetsExporting}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md px-3 py-1.5 bg-white disabled:opacity-50"
          >
            <UploadCloud className="h-4 w-4" /> {sheetsExporting ? 'Exporting…' : '→ Sheets'}
          </button>
          )}
          <button onClick={() => setModal('new')} className="btn-primary">
            <Plus className="h-4 w-4 mr-1" /> New Bid
          </button>
        </div>
      </div>
      {sheetsMsg && (
        <div className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-sm text-blue-800">
          {sheetsMsg}
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input type="search" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search bids: name, address, phone, title or #number"
          className="input pl-9 w-full" />
      </div>

      <BidStartDays onOpenBid={id => { setSelectedQuote({ id }); setModal('detail'); }} />

      {/* Status filter */}
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setFilterStatus('')}
          className={`press px-3 py-1.5 text-sm rounded-full font-medium transition-colors ${!filterStatus ? 'bg-brand-600 text-black' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
          All
        </button>
        {STATUSES.map(s => (
          <button key={s} onClick={() => setFilterStatus(s)}
            className={`press px-3 py-1.5 text-sm rounded-full font-medium transition-colors ${filterStatus === s ? 'bg-brand-600 text-black' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {s}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-center text-gray-500 py-8">Loading…</div>
      ) : quotes.length === 0 ? (
        <div className="card p-8 text-center text-gray-500">{words.length ? `No bids match "${search.trim()}".` : 'No quotes yet. Create your first one!'}</div>
      ) : (
        <div className="space-y-3">
          {quotes.map((q: any) => (
            <div key={q.id} className="card px-4 py-4 hover:bg-gray-50 cursor-pointer" onClick={() => { setSelectedQuote(q); setModal('detail'); }}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900">{q.title}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[q.status] || 'bg-gray-100 text-gray-600'}`}>{q.status}</span>
                    {Number(q.overgrowth_fee) > 0 && (
                      <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-amber-100 text-amber-900 border border-amber-300" title="A one-time catch-up on the first visit, on top of the mow">
                        Overgrown · +{fmtMoney(q.overgrowth_fee)} first visit
                      </span>
                    )}
                  </div>
                  <div className="flex gap-3 mt-1">
                    <span className="text-sm text-gray-600">{q.customer_name}</span>
                    {q.address && <span className="text-xs text-gray-500">{q.address.split(',')[0]}</span>}
                  </div>
                  {openedWords(q) && <p className={`text-xs mt-0.5 ${Number(q.view_count) > 0 ? 'text-blue-700 font-medium' : 'text-gray-500'}`}>{openedWords(q)}</p>}
                  {q.total > 0 && (
                    <p className="text-sm font-semibold text-green-700 mt-1">
                      {q.service_frequency && Number(q.mow_price) > 0
                        ? <>{fmtMoney(q.mow_price)}/mow · {String(q.service_frequency).replace('_', ' ')}{Number(q.total) > Number(q.mow_price) ? <span className="text-gray-500 font-normal"> · {fmtMoney(q.total)} a visit with travel</span> : null}</>
                        // No per-mow price on the bid: the total is a bid total,
                        // never "$180/mow" for a six-mow bid (Sep 28 2026).
                        : <>{fmtMoney(q.total)}{q.service_frequency ? <span className="text-gray-500 font-normal"> total · {String(q.service_frequency).replace('_', ' ')}</span> : null}</>}
                    </p>
                  )}
                </div>
                <button onClick={e => {
                  e.stopPropagation();
                  if (window.confirm(`Delete "${q.title}"?`)) deleteQuote.mutate(q.id);
                }} className="p-1.5 text-gray-500 hover:text-red-500 rounded flex-shrink-0" title="Delete">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* New quote modal */}
      {modal === 'new' && (
        <Modal title="New Bid" onClose={() => { setModal(null); setCustomerSearch(''); }}>
          <div className="space-y-4">
            <Field label="Customer *">
              {newForm.customer_id ? (
                <div className="flex items-center justify-between border border-brand-300 bg-brand-50 rounded-md px-3 py-2 text-sm">
                  <span className="font-medium text-brand-900">
                    {customers.find((c: any) => String(c.id) === newForm.customer_id)?.name}
                    <AccountMark mark={customers.find((c: any) => String(c.id) === newForm.customer_id)?.account_mark} />
                  </span>
                  <button type="button"
                    onClick={() => { setNewForm(f => ({ ...f, customer_id: '', property_id: '' })); setCustomerSearch(''); }}
                    className="text-xs text-brand-600 hover:text-brand-800 ml-2">
                    Change
                  </button>
                </div>
              ) : (
                <div className="relative space-y-2">
                  {newClient ? (
                <div className="rounded-lg border border-brand-200 bg-brand-50/60 p-3">
                  <CustomerIntake compact ai={!isBidder} source="bid" title="New client (on site)"
                    prefill={{ name: newClient.name }}
                    onCancel={() => setNewClient(null)}
                    submitLabel="Add & quote them"
                    onSaved={out => {
                      qc.invalidateQueries(['customers-list', isBidder]);
                      setNewForm(f => ({ ...f, customer_id: String(out.customer.id), property_id: out.property?.id ? String(out.property.id) : '' }));
                      setNewClient(null); setCustomerSearch('');
                      toast.success(`${out.customer.name} added`);
                    }} />
                </div>
              ) : (
                <button type="button" onClick={() => setNewClient({ ...emptyClient, name: customerSearch.trim() })}
                  className="w-full text-left text-sm text-brand-700 font-medium px-3 py-2 rounded-lg border border-dashed border-brand-300 bg-brand-50 hover:bg-brand-100">
                  + New client — I'm on site, add them now
                </button>
              )}
                  {!newClient && <input type="text" className="input" placeholder="Search customers…"
                    value={customerSearch} onChange={e => setCustomerSearch(e.target.value)} autoFocus />}
                  {customers.length > 0 && !newClient && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {(customerSearch
                        ? customers.filter((c: any) => c.name.toLowerCase().includes(customerSearch.toLowerCase()))
                        : customers
                      ).map((c: any) => (
                        <button key={c.id} type="button"
                          className="w-full text-left px-3 py-2 text-sm hover:bg-brand-50 hover:text-brand-900"
                          onClick={() => { setNewForm(f => ({ ...f, customer_id: String(c.id), property_id: '' })); setCustomerSearch(''); }}>
                          {c.name}<AccountMark mark={c.account_mark} />
                        </button>
                      ))}
                      {customerSearch && customers.filter((c: any) => c.name.toLowerCase().includes(customerSearch.toLowerCase())).length === 0 && (
                        <p className="px-3 py-2 text-sm text-gray-500">No customers match</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </Field>
            <Field label="Property">
              <select value={newForm.property_id} onChange={e => setNewForm(f => ({ ...f, property_id: e.target.value }))} className="input" disabled={!newForm.customer_id}>
                <option value="">— Select property —</option>
                {properties.map((p: any) => {
                  const addr = [p.address, p.city, p.state].filter(Boolean).join(', ');
                  return <option key={p.id} value={p.id}>{p.label ? `${p.label} — ${addr}` : addr}</option>;
                })}
              </select>
            </Field>
            <Field label="What kind of bid?">
              <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm font-medium">
                <button type="button" onClick={() => setBidKind('mowing')} className={`press flex-1 px-3 py-2 ${bidKind === 'mowing' ? 'bg-brand-600 text-black' : 'bg-white text-gray-700'}`}>Mowing — a price per visit</button>
                <button type="button" onClick={() => setBidKind('lines')} className={`press flex-1 px-3 py-2 border-l border-gray-300 ${bidKind === 'lines' ? 'bg-brand-600 text-black' : 'bg-white text-gray-700'}`}>Project — line items</button>
              </div>
            </Field>
            {bidKind === 'mowing' && (() => {
              const yard = properties.find((p: any) => String(p.id) === newForm.property_id);
              const yardPrice = Number(yard?.mowing_price) > 0 ? Number(yard.mowing_price) : null;
              return (
                <div className="rounded-lg border border-brand-200 bg-brand-50/50 p-3 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <label className="text-xs text-gray-600">How often
                      <select value={mow.frequency} onChange={e => setMow(m => ({ ...m, frequency: e.target.value }))} className="input mt-1">
                        <option value="weekly">Every week</option>
                        <option value="biweekly">Every 2 weeks</option>
                        <option value="monthly">Every 4 weeks</option>
                        <option value="one_time">One time</option>
                      </select>
                    </label>
                    <label className="text-xs text-gray-600">Price per mow ($)
                      <input type="number" inputMode="decimal" min="0" value={mow.price} onChange={e => setMow(m => ({ ...m, price: e.target.value }))} className="input mt-1" placeholder={yardPrice ? `${yardPrice} (the yard's price)` : 'e.g. 45'} />
                    </label>
                    <label className="text-xs text-gray-600">One-time fee ($)
                      <input type="number" inputMode="decimal" min="0" value={mow.one_time_fee} onChange={e => setMow(m => ({ ...m, one_time_fee: e.target.value }))} className="input mt-1" placeholder="0 — credited back after the 3rd mow" />
                    </label>
                    <label className={`text-xs font-medium ${Number(mow.overgrowth) > 0 ? 'text-amber-900' : 'text-gray-600'}`}>Overgrown? one-time catch-up ($)
                      <input type="number" inputMode="decimal" min="0" value={mow.overgrowth} onChange={e => setMow(m => ({ ...m, overgrowth: e.target.value }))}
                        className={`input mt-1 ${Number(mow.overgrowth) > 0 ? 'border-amber-400 bg-amber-50 font-semibold' : ''}`} placeholder="0 — blank if the lawn is not overgrown" />
                      <span className="block mt-0.5 text-[10px] font-normal text-gray-500">Charged on the first visit only, on top of the mow. Never credited back.</span>
                    </label>
                    <label className="text-xs text-gray-600">First mow
                      <span className="flex gap-1.5 mt-1">
                        <input type="date" value={mow.start_date} onChange={e => setMow(m => ({ ...m, start_date: e.target.value }))} className="input flex-1 min-w-0" />
                        <input type="time" value={mow.start_time} onChange={e => setMow(m => ({ ...m, start_time: e.target.value }))} className="input w-28" />
                      </span>
                    </label>
                  </div>
                  <p className="text-[11px] text-gray-500">
                    {!newForm.property_id ? 'Pick the yard above — a mowing bid is for one yard.'
                      : mow.price === '' ? (yardPrice ? `Blank uses the yard's price, $${yardPrice}.` : 'Blank uses your price bands for this lot; if none fits you will be asked to type it.')
                      : 'Weekly takes your weekly discount off this price, the same as a bid made in the yard.'}
                    {' '}A yard outside your area gets its travel line on its own. When they accept, it goes on the route — nothing to convert.
                  </p>
                  {(() => {
                    const p = Number(mow.price || yardPrice) || 0, fee = Number(mow.one_time_fee) || 0, og = Number(mow.overgrowth) || 0;
                    if (!(p > 0) || (!(fee > 0) && !(og > 0))) return null;
                    const $ = (n: number) => `$${Math.round(n * 100) / 100}`;
                    return (<>
                      {og > 0 && (
                        <p className="text-sm font-semibold text-amber-900 bg-amber-50 border border-amber-300 rounded-lg px-2.5 py-1.5">
                          Overgrown: first visit {$(p + fee + og)} — includes a one-time {$(og)} catch-up{fee > 0 ? ` and the ${$(fee)} fee` : ''}. {mow.frequency === 'one_time' ? 'That is the whole price.' : `Then ${$(p)} a mow.`}
                        </p>
                      )}
                      {fee > 0 && (
                      <p className="text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1.5">
                        {mow.frequency === 'one_time'
                          ? <>One-time mow {$(p + fee + og)}{og ? ` (with the ${$(og)} catch-up)` : ''}.</>
                          : mow.frequency === 'monthly'
                          ? <>Mow 1 {$(p + fee + og)}, then {$(p)}. The credit after three mows is for weekly and every-2-weeks yards — those go on the route by themselves when accepted.</>
                          : <>The bid leads with the one-time price, {$(p + fee)}. If they go recurring: mow 1 {$(p + fee + og)} · mow 2 {$(p)} · mow 3 {$(p - fee)} (the {$(fee)} comes back) · then {$(p)}. No contract.{og ? ` The ${$(og)} catch-up is never credited.` : ''} Travel, if any, is added to mow 1 and credited with the fee.{mow.frequency === 'weekly' ? ' Your weekly discount comes off the mow price first.' : ''}</>}
                      </p>
                      )}
                    </>);
                  })()}
                </div>
              );
            })()}
            {bidKind === 'lines' && (
            <Field label="Bid title *">
              <input value={newForm.title} onChange={e => setNewForm(f => ({ ...f, title: e.target.value }))} className="input" placeholder="e.g. Spring landscaping proposal" />
            </Field>
            )}
            {bidKind === 'lines' && (
            <Field label="Intro Note">
              <textarea value={newForm.intro_note} onChange={e => setNewForm(f => ({ ...f, intro_note: e.target.value }))} className="input" rows={2} placeholder="Optional note at top of quote" />
            </Field>
            )}
            {/* Offer the lawn plans on this bid? The default comes from Settings → Bids; this is the one bid. */}
            <label className="flex items-start gap-3 cursor-pointer rounded-lg border border-gray-200 px-3 py-2">
              <input type="checkbox" className="mt-1 h-4 w-4 accent-brand-600" checked={offerPlansOn} onChange={e => setOfferPlans(e.target.checked)} />
              <span className="text-sm text-gray-800">Offer lawn plans on this bid
                <span className="block text-[11px] text-gray-500">{offerPlansOn ? 'Their page offers the three lawn plans above Accept.' : 'Just the work on the bid — no lawn plans on their page.'}{offerPlans === null ? ' (your default)' : ''}</span>
              </span>
            </label>
            <Field label="Internal Notes">
              <textarea value={newForm.notes} onChange={e => setNewForm(f => ({ ...f, notes: e.target.value }))} className="input" rows={2} />
            </Field>
            <Field label="Referral code">
              <input value={newForm.referral_code} onChange={e => setNewForm(f => ({ ...f, referral_code: e.target.value.toUpperCase() }))} className="input" placeholder="If a neighbor sent them (optional)" />
            </Field>
            {bidKind === 'lines' && (<>
            {/* The lines on the bid, typed right here: what, how many, price each. More can be added on the bid afterwards. */}
            <Field label="Line items">
              <div className="space-y-1.5">
                <div className="hidden sm:grid grid-cols-[1fr_72px_112px_28px] gap-2 text-[11px] uppercase tracking-wide text-gray-500 px-0.5"><span>Item</span><span>Qty</span><span>Price each</span><span /></div>
                {newLines.map((l, i) => {
                  const set = (patch: Partial<NewLine>) => setNewLines(ls => ls.map((x, j) => j === i ? { ...x, ...patch } : x));
                  return (
                    <div key={i} className="space-y-1">
                      <div className="grid grid-cols-[1fr_64px_100px_28px] sm:grid-cols-[1fr_72px_112px_28px] gap-2 items-center">
                        <input value={l.description} onChange={e => set({ description: e.target.value })} className="input" placeholder={i === 0 ? 'e.g. Biweekly mowing: mow, edge, trim, and blow-off' : 'Another item'} />
                        <input type="number" inputMode="numeric" min="1" value={l.quantity} onChange={e => set({ quantity: e.target.value })} className="input" placeholder="1" />
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                          <input type="number" inputMode="decimal" min="0" value={l.price} onChange={e => set({ price: e.target.value })} className="input pl-5" placeholder="0" />
                        </div>
                        <button type="button" onClick={() => setNewLines(ls => ls.length > 1 ? ls.filter((_, j) => j !== i) : [blankLine()])} className="text-gray-500 hover:text-red-600" title="Remove"><X className="h-4 w-4" /></button>
                      </div>
                      {/* Priced one-time, per visit, or per month — per month asks how often the work is done (the rhythm). */}
                      <div className="flex flex-wrap items-center gap-2 pl-0.5">
                        <select value={l.unit} onChange={e => set({ unit: e.target.value as NewLine['unit'] })} className="input text-sm w-36" title="Priced per">
                          <option value="">one-time</option><option value="visit">per visit</option><option value="month">per month</option>
                        </select>
                        {l.unit === 'month' && <RhythmFields n={l.every_n} unit={l.every_unit} firstOn={l.first_on} onChange={patch => set(patch)} />}
                      </div>
                      {l.sub ? (
                        <div className="flex flex-wrap items-center gap-2 pl-0.5">
                          <SubFields cost={l.cost} pct={l.pct} onChange={(cost, pct) => { rememberMarkup(pct); set({ cost, pct, price: markedUp(cost, pct) || l.price }); }} />
                          <button type="button" onClick={() => set({ sub: false, cost: '', pct: '' })} className="text-xs text-gray-500 underline">Not subcontracted</button>
                        </div>
                      ) : (
                        <button type="button" onClick={() => set({ sub: true, pct: lastMarkup() })} className="text-[11px] text-gray-500 hover:text-brand-700 pl-0.5">Subcontracted? Price it from the sub's cost + a markup</button>
                      )}
                    </div>
                  );
                })}
                <div className="flex items-center justify-between">
                  <button type="button" onClick={() => setNewLines(ls => [...ls, blankLine()])} className="text-xs font-medium text-brand-700 hover:text-brand-900">+ Add a line</button>
                  {newLines.some(l => l.description.trim() && Number(l.price) > 0) && <span className="text-xs text-gray-500">Total ${newLines.reduce((t, l) => t + (l.description.trim() ? (Number(l.price) || 0) * Math.max(1, Number(l.quantity) || 1) : 0), 0).toFixed(2)}</span>}
                </div>
              </div>
            </Field>
            {/* Or: paste the bid notes and let the app write the line items. */}
            <div className="rounded-lg border border-dashed border-brand-300 bg-brand-50/50 p-3 space-y-2">
              <p className="text-sm font-medium text-brand-800">Draft it from your notes</p>
              <textarea value={aiText} onChange={e => setAiText(e.target.value)} className="input" rows={4}
                placeholder={"Sod install — $1,200\n3 x pallets @ $250\nOptional: landscape lighting $400\nZoysia +$250/pallet or St. Augustine +$225/pallet"} />
              <div className="flex items-center justify-between gap-2">
                <p className="text-[11px] text-gray-500">One item per line with its price. Choices and add-ons are picked up too. You check it before it goes out.</p>
                <button type="button" className="btn-primary text-xs whitespace-nowrap" disabled={!newForm.customer_id || aiText.trim().length < 10 || aiDraft.isLoading} onClick={() => aiDraft.mutate()}>
                  {aiDraft.isLoading ? 'Drafting…' : 'Draft the bid'}
                </button>
              </div>
              {(!newForm.customer_id || aiText.trim().length < 10) && (
                <p className="text-[11px] text-amber-700">{!newForm.customer_id ? 'Pick the customer first, then Draft the bid lights up.' : 'Type at least one line with a price to draft from.'}</p>
              )}
            </div>
            </>)}
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => { setModal(null); setCustomerSearch(''); }} className="btn-secondary">Cancel</button>
              {bidKind === 'mowing' ? (
                <button onClick={() => createMowing.mutate()} disabled={createMowing.isLoading || !newForm.customer_id || !newForm.property_id} className="btn-primary">
                  {createMowing.isLoading ? 'Drafting…' : 'Create mowing bid'}
                </button>
              ) : (
              <button onClick={() => createQuote.mutate()} disabled={createQuote.isLoading || !newForm.customer_id || !newForm.title}
                className="btn-primary">
                {createQuote.isLoading ? 'Creating…' : 'Create bid'}
              </button>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* Detail modal */}
      {modal === 'detail' && selectedQuote && (
        <QuoteDetailModal
          quote={quote}
          schedule={schedule}
          oneTimeMow={oneTimeMow}
          convertedJob={converted_job}
          convertedProject={converted_project}
          deposit={depositInfo}
          winterDeal={winterDeal}
          sizesDeal={sizesDeal}
          bushPlan={bushPlan}
          lineItems={lineItems}
          bidOptions={bidOptions}
          groups={groups}
          media={media}
          checklist={checklist}
          changeOrders={changeOrders}
          followup={detailData?.followup || null}
          catalog={catalog}
          routes={routes}
          crews={crews}
          members={members}
          onClose={() => { setModal(null); setSelectedQuote(null); }}
          onRefresh={refetchDetail}
          onStatusChange={(status: string) => updateStatus.mutate({ id: selectedQuote.id, status })}
          onDelete={() => deleteQuote.mutate(selectedQuote.id)}
          smsConfigured={smsStatus?.configured ?? false}
        />
      )}
    </div>
  );
}

// ── Quote detail modal ────────────────────────────────────────────────────────
/**
 * The site map on a bid (owner, Oct 27 2026: "make a map of the lawn for bids, so it shows where the work gets done.
 * It's not required… just for some lawns that are bigger"). The map belongs to the yard (/site-map/:propertyId); this
 * card says how much is drawn and holds the one switch that puts it on the customer's page as "Where the work gets
 * done" — areas and notes only, never a lock, a gate or a code (server siteMarks.customerMarks). Owner only.
 */
function BidSiteMapCard({ quote, onRefresh }: { quote: any; onRefresh: () => void }) {
  const qc = useQueryClient();
  const key = ['quote-site-map', quote?.id];
  const { data, isLoading } = useQuery(key, () => api.get(`/quotes/${quote.id}/site-map`).then(r => r.data as {
    property_id: number | null; customer_id: number | null; on: boolean; pin: { lat: number; lng: number } | null; counts: string; shown: number;
  }), { enabled: !!quote?.id, retry: false });
  const flip = useMutation(() => api.post(`/quotes/${quote.id}/site-map`, { on: !data?.on }).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d?.message || 'Saved', { autoClose: 7000 }); qc.invalidateQueries(key); onRefresh(); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not save that'); },
  });
  if (!quote?.id) return null;
  const on = !!data?.on;
  const drawn = (data?.shown ?? 0) > 0;
  return (
    <div className="rounded-xl border border-gray-200 p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-bold text-gray-900 flex items-center gap-1.5"><MapIcon className="h-4 w-4 text-gray-500" /> Site map</p>
        {isLoading && <span className="text-xs text-gray-400">Loading…</span>}
      </div>
      {!isLoading && !data?.property_id ? (
        <p className="text-xs text-gray-500">
          Pin the yard first — this bid has no yard on it.{' '}
          {(quote.customer_id || data?.customer_id) && <Link to={`/customers?profile=${quote.customer_id || data?.customer_id}`} className="text-brand-700 font-semibold underline">Open the customer</Link>}
        </p>
      ) : !isLoading && data ? (
        <>
          <p className="text-xs text-gray-500">
            {data.counts ? `Drawn: ${data.counts}.` : 'Nothing drawn yet.'}{' '}
            {data.counts && !drawn ? 'Only areas and notes go on their bid — locks and gates never do.' : ''}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Link to={`/site-map/${data.property_id}`} className="btn-secondary text-sm flex items-center gap-1.5">
              <MapIcon className="h-3.5 w-3.5" /> {data.counts ? 'Edit the site map' : 'Draw the site map'}
            </Link>
            <button type="button" onClick={() => flip.mutate()} disabled={flip.isLoading}
              role="switch" aria-checked={on}
              className={`flex-1 min-w-[12rem] py-2 rounded-xl text-sm font-semibold border ${on ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-900 border-gray-300'} disabled:opacity-80`}>
              {flip.isLoading ? 'Saving…' : on ? 'Show it on the bid ✓' : 'Show it on the bid'}
            </button>
          </div>
          <p className="text-xs text-gray-500">
            {on
              ? (drawn ? 'Their page shows "Where the work gets done": the areas with their work, and any notes. Locks, gates and codes never show.'
                       : 'On, but nothing to show yet — draw an area and it appears on their page.')
              : 'Off. Optional — for the bigger lawns that need one.'}
          </p>
        </>
      ) : null}
    </div>
  );
}

function QuoteDetailModal({
  quote, lineItems, bidOptions = [], groups, media, checklist, catalog, routes, crews, members,
  onClose, onRefresh, onStatusChange, onDelete, smsConfigured, schedule = null, oneTimeMow = null, convertedJob = null, convertedProject = null, deposit = null, changeOrders = null,
  followup = null, winterDeal = null, sizesDeal = null, bushPlan = null,
}: {
  quote: any; lineItems: any[]; groups: any[]; media: any[]; checklist: any[]; schedule?: any;
  /** Several options on one bid (services/bidOptions): each with its own title, photos and priced lines. */
  bidOptions?: BuilderOption[]; oneTimeMow?: any; convertedJob?: any; convertedProject?: any; deposit?: any; changeOrders?: any;
  /** The winter deal (services/winterWork): on, the percent, whether this bid can carry it, which lines could (the snowflake). */
  winterDeal?: WinterDeal | null;
  /** The plant sizes a line can be offered in (services/bushSizes): the catalog, and what each line offers now. */
  sizesDeal?: SizesDeal | null;
  /** The care plan after a planting (services/bushFollowUp): the 3 / 4 / 6 month prices the care note offers. */
  bushPlan?: BushPlan | null;
  // The sent bid's follow-ups in the server's words (services/bidFollowUps.statusFor): "Follow-up 1 of 2 sent Thu · next follow-up Sat".
  followup?: { words: string; stopped: boolean; can_stop: boolean } | null;
  catalog: any[]; routes: any[]; crews: any[]; members: any[];
  onClose: () => void; onRefresh: () => void;
  onStatusChange: (s: string) => void; onDelete: () => void;
  smsConfigured?: boolean;
}) {
  const navigate = useNavigate();
  // Does accepting this bid book a mow? (The same answer the Schedule rows below read — one request, shared.) An
  // every-4-weeks or one-time bid whose mow line was taken off books none: "Put on the mowing schedule" would answer
  // "make the job instead" with no such button left on the screen (review, Oct 6 2026).
  const { data: dayOpts } = useQuery(['quote-day-options', quote?.id], () => api.get(`/quotes/${quote.id}/day-options`).then(r => r.data), { enabled: !!quote?.property_id });
  const noMowLine = dayOpts?.books_mow === false && ['monthly', 'one_time'].includes(String(quote?.service_frequency || ''));
  // "Stop follow-ups" on this one bid (and back on): no more texts and no call to-do (services/bidFollowUps).
  const followupSwitch = useMutation(() => api.put(`/quotes/${quote.id}/followups`, { off: !followup?.stopped }).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d?.message || 'Saved'); onRefresh(); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not save that'); },
  });
  const visitHours = useMutation((body: any) => api.put(`/quotes/${quote.id}/visit-hours`, body), {
    onSuccess: () => { toast.success('Visit hours saved'); onRefresh(); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not save those hours'); },
  });
  const requireCard = useMutation(() => api.put(`/quotes/${quote.id}/require-card`, { on: !quote.require_card }).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d?.message || 'Saved'); onRefresh(); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not save that'); },
  });
  // "Require 50% upfront" (owner, Sep 29 2026): paid by card on the bid before they can accept; off the final bill.
  // "Offer lawn plans on this bid" (owner, Oct 22 2026). Draft and sent bids only — an accepted bid is what they signed.
  const offerPlansSwitch = useMutation(() => api.put(`/quotes/${quote.id}/offer-plans`, { on: Number(quote.offer_plans ?? 1) === 0 }).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d.message || 'Saved'); onRefresh(); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not save that'); },
  });
  const requireDeposit = useMutation(() => api.put(`/quotes/${quote.id}/require-deposit`, { on: !Number(quote.require_deposit) }).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d?.message || 'Saved', { autoClose: 7000 }); onRefresh(); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not save that'); },
  });
  const [depositForm, setDepositForm] = useState<{ open: boolean; method: string; amount: string }>({ open: false, method: 'cash', amount: '' });
  const depositReceived = useMutation(() => api.post(`/quotes/${quote.id}/deposit-received`, { method: depositForm.method, amount: depositForm.amount }).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d?.message || 'Recorded'); setDepositForm({ open: false, method: 'cash', amount: '' }); onRefresh(); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not record that'); },
  });
  const [addingItem, setAddingItem] = useState(false);
  const blankItem = { description: '', quantity: '1', unit_price: '', is_optional: false, service_catalog_id: '', unit: '', sub: false, cost: '', pct: '', on_request: false, every_n: '', every_unit: 'months' as EveryUnit, first_on: '' };
  const [itemForm, setItemForm] = useState(blankItem);
  const [editingItem, setEditingItem] = useState<any | null>(null);
  const [addingGroup, setAddingGroup] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [newCheck, setNewCheck] = useState('');
  async function addCheck() {
    const label = newCheck.trim();
    if (!label) return;
    try { await api.post(`/quotes/${quote.id}/checklist`, { label }); setNewCheck(''); onRefresh(); }
    catch { toast.error('Could not add that'); }
  }
  async function toggleCheck(it: any) {
    try { await api.put(`/quotes/checklist/${it.id}`, { is_done: it.is_done ? 0 : 1 }); onRefresh(); }
    catch { toast.error('Could not update that'); }
  }
  async function delCheck(it: any) {
    try { await api.delete(`/quotes/checklist/${it.id}`); onRefresh(); }
    catch { toast.error('Could not remove that'); }
  }
  const [addingOption, setAddingOption] = useState<number | null>(null);
  const [optionForm, setOptionForm] = useState({ name: '', description: '', price: '' });
  const [showSend, setShowSend] = useState(false);
  // One tap copies the customer's link (minting it if the bid has none yet).
  const [linkCopied, setLinkCopied] = useState(false);
  const refreshCopy = useMutation(() => api.post(`/quotes/${quote.id}/refresh-copy`, {}), {
    onSuccess: (r: any) => { toast.success(r.data?.message || 'Wording refreshed'); onRefresh(); qc.invalidateQueries('quotes'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not refresh the wording'); },
  });
  const putOnSchedule = useMutation(() => api.post(`/quotes/${quote.id}/schedule`, {}).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d?.message || 'Scheduled', { autoClose: 8000 }); onRefresh(); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not schedule it'); },
  });
  async function copyLink() {
    try {
      const r = await api.post(`/quotes/${quote.id}/share-token`);
      const url = `${window.location.origin}/q/${r.data?.token}`;
      await navigator.clipboard.writeText(url);
      setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2000);
      toast.success('Link copied — paste it into a text or email');
    } catch (e: any) { toast.error(e?.response?.data?.message || 'Could not copy the link'); }
  }
  const [showConvert, setShowConvert] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiText, setAiText] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [scopeBusy, setScopeBusy] = useState(false);
  const [scopeEditing, setScopeEditing] = useState(false);
  const [scopeDraft, setScopeDraft] = useState('');
  const qc = useQueryClient();

  async function generateScope() {
    setScopeBusy(true);
    try {
      const r = await api.post('/ai/scope-of-work', { quote_id: quote.id });
      setScopeDraft(r.data.scope_of_work || '');
      setScopeEditing(false);
      onRefresh(); qc.invalidateQueries('quotes');
      toast.success('Scope of work drafted — read it before sending');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not draft the scope');
    } finally { setScopeBusy(false); }
  }

  async function saveScope() {
    try {
      await api.put(`/quotes/${quote.id}`, { scope_of_work: scopeDraft });
      setScopeEditing(false);
      onRefresh(); qc.invalidateQueries('quotes');
      toast.success('Scope saved');
    } catch { toast.error('Could not save'); }
  }

  // Quote flavor of the AI builder: either/or rows and upgrades come in as
  // OPTIONAL line items (quotes support that natively) — nothing pre-billed.
  async function buildQuoteWithAi() {
    const raw = aiText.trim();
    if (!raw) return;
    setAiBusy(true);
    try {
      const r = await api.post('/ai/itemize-quote', { text: raw, service_type: quote.title || undefined });
      const rows = (r.data.line_items || []).filter((li: any) => li.description && li.unit_price != null);
      for (const li of rows) {
        await api.post(`/quotes/${quote.id}/line-items`, {
          description: `${li.section != null ? `${li.section}. ` : ''}${li.description}${li.free ? ' (FREE)' : ''}`,
          quantity: Number(li.quantity) || 1,
          unit_price: Number(li.unit_price) || 0,
          is_optional: li.optional ? 1 : 0,
          is_selected: li.optional ? 0 : 1,
          service_catalog_id: null,
        });
      }
      onRefresh(); qc.invalidateQueries('quotes');
      setAiOpen(false); setAiText('');
      toast.success(`${rows.length} quote lines built — optional work marked optional`);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'AI quote failed — try again');
    } finally { setAiBusy(false); }
  }

  const addItem = useMutation(async () => {
    const qty = Number(itemForm.quantity || 1);
    const price = itemForm.on_request ? 0 : Number(itemForm.unit_price);
    await api.post(`/quotes/${quote.id}/line-items`, {
      description: itemForm.description, quantity: qty, unit_price: price,
      is_optional: itemForm.is_optional || itemForm.on_request ? 1 : 0, is_selected: itemForm.on_request ? 0 : 1,
      quote_on_request: itemForm.on_request ? 1 : 0,
      service_catalog_id: itemForm.service_catalog_id ? Number(itemForm.service_catalog_id) : null,
      unit: itemForm.unit || null,
      sub_cost: itemForm.sub && Number(itemForm.cost) > 0 ? Number(itemForm.cost) : null,
      markup_pct: itemForm.sub ? Number(itemForm.pct) || 0 : null,
      // Billed per month: how often it is done (server: services/monthlyLine).
      every_n: itemForm.unit === 'month' ? Number(itemForm.every_n) || null : null,
      every_unit: itemForm.unit === 'month' ? itemForm.every_unit : null,
      first_on: itemForm.unit === 'month' && itemForm.first_on ? itemForm.first_on : null,
    });
  }, {
    onSuccess: () => {
      onRefresh(); qc.invalidateQueries('quotes'); setAddingItem(false);
      setItemForm(blankItem);
    },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to add'); },
  });

  const updateItem = useMutation(async (item: any) => {
    await api.put(`/quotes/line-items/${item.id}`, {
      description: item.description, quantity: Number(item.quantity),
      unit_price: item.quote_on_request ? 0 : Number(item.unit_price), is_optional: item.is_optional || item.quote_on_request ? 1 : 0,
      quote_on_request: item.quote_on_request ? 1 : 0,
      unit: item.unit || null,
      sub_cost: item.sub_cost != null && item.sub_cost !== '' && Number(item.sub_cost) > 0 ? Number(item.sub_cost) : null,
      markup_pct: item.sub_cost != null && item.sub_cost !== '' ? Number(item.markup_pct) || 0 : null,
      every_n: item.unit === 'month' ? Number(item.every_n) || null : null,
      every_unit: item.unit === 'month' ? (item.every_unit || 'months') : null,
      first_on: item.unit === 'month' && item.first_on ? String(item.first_on).slice(0, 10) : null,
    });
  }, {
    onSuccess: () => { onRefresh(); qc.invalidateQueries('quotes'); setEditingItem(null); },
    onError: () => { toast.error('Failed to update'); },
  });

  const toggleItemSelected = useMutation(async ({ id, is_selected }: { id: number; is_selected: boolean }) => {
    await api.put(`/quotes/line-items/${id}`, { is_selected: is_selected ? 1 : 0 });
  }, {
    onSuccess: () => { onRefresh(); qc.invalidateQueries('quotes'); },
  });
  // The snowflake on a line: offer it in winter at the discount (quote_line_items.winter_offer). The customer chooses Now / In winter on the bid.
  const toggleWinterOffer = useMutation(async ({ id, on }: { id: number; on: boolean }) => {
    await api.put(`/quotes/line-items/${id}`, { winter_offer: on ? 1 : 0 });
  }, {
    onSuccess: (_d, v) => { onRefresh(); qc.invalidateQueries('quotes'); toast.success(v.on ? `Offered in winter — ${winterDeal?.pct ?? 10}% off if they wait` : 'Winter offer taken off'); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not change the winter offer'); },
  });
  // The sizes offered on one planting line (quote_line_items.size_options; PUT /quotes/line-items/:id/sizes). The
  // customer picks one on their bid and the total follows; an empty list takes the sizes off and the price stays.
  const [sizingLine, setSizingLine] = useState<number | null>(null);
  const saveSizes = useMutation(async ({ id, sizes, keep }: { id: number; sizes: SizeOption[]; keep: string | null }) =>
    (await api.put(`/quotes/line-items/${id}/sizes`, { sizes, keep })).data, {
    onSuccess: (d: any) => { setSizingLine(null); onRefresh(); qc.invalidateQueries('quotes'); toast.success(d?.message || 'Saved'); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not save the sizes'); },
  });
  const sizesOf = (li: any) => sizesDeal?.offered?.[String(li.id)] || null;
  const canSize = (li: any) => !!sizesDeal && (sizesDeal.can_offer.includes(li.id) || !!sizesOf(li));
  // The care plan this bid offers after a planting (services/bushFollowUp; PUT /quotes/:id/bush-plan): $ a visit at
  // every 3, 4 and 6 months. The customer picks one on the care page the finished planting sends them.
  const [careOpen, setCareOpen] = useState(false);
  const [carePrices, setCarePrices] = useState<Record<string, string>>({});
  const careValue = (m: number) => (carePrices[String(m)] !== undefined ? carePrices[String(m)] : (bushPlan?.prices?.[String(m)] != null ? String(bushPlan!.prices![String(m)]) : ''));
  const saveCarePlan = useMutation(async () => {
    const body: Record<string, string> = {};
    for (const m of bushPlan?.months || [3, 4, 6]) body[`every_${m}`] = careValue(m);
    return (await api.put(`/quotes/${quote.id}/bush-plan`, body)).data;
  }, {
    onSuccess: (d: any) => { setCarePrices({}); onRefresh(); qc.invalidateQueries('quotes'); toast.success(d?.message || 'Saved'); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not save those prices'); },
  });
  const winterPriceOf = (li: any) => { const pct = winterDeal?.pct ?? 10; return Math.round(Number(li.total) * (1 - pct / 100) * 100) / 100; };
  // Does the line carry the offer as things stand? The server decides (the setting's default, the snowflake, a line
  // already chosen) — the editor only reads its list.
  const winterOffered = (li: any) => (winterDeal?.offered_ids ? winterDeal.offered_ids.includes(li.id) : Number(li.winter_offer) === 1);
  // "Offer winter prices on this bid": the whole bid at once (quotes.winter_off, PUT /quotes/:id/winter-offer).
  const winterBidSwitch = useMutation((on: boolean) => api.put(`/quotes/${quote.id}/winter-offer`, { on }).then(r => r.data), {
    onSuccess: (d: any) => { onRefresh(); qc.invalidateQueries('quotes'); toast.success(d?.message || 'Saved'); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not change that'); },
  });

  const deleteItem = useMutation(async (id: number) => { await api.delete(`/quotes/line-items/${id}`); }, {
    onSuccess: () => { onRefresh(); qc.invalidateQueries('quotes'); },
    onError: () => { toast.error('Failed to delete'); },
  });

  const addGroup = useMutation(async () => {
    await api.post(`/quotes/${quote.id}/option-groups`, { name: groupName });
  }, {
    onSuccess: () => { onRefresh(); qc.invalidateQueries('quotes'); setAddingGroup(false); setGroupName(''); },
    onError: () => { toast.error('Failed to add group'); },
  });

  const deleteGroup = useMutation(async (id: number) => { await api.delete(`/quotes/option-groups/${id}`); }, {
    onSuccess: () => { onRefresh(); qc.invalidateQueries('quotes'); },
    onError: () => { toast.error('Failed to delete group'); },
  });

  const addOption = useMutation(async () => {
    await api.post(`/quotes/option-groups/${addingOption}/options`, {
      name: optionForm.name, description: optionForm.description,
      price: optionForm.price ? Number(optionForm.price) : 0,
    });
  }, {
    onSuccess: () => {
      onRefresh(); qc.invalidateQueries('quotes');
      setAddingOption(null); setOptionForm({ name: '', description: '', price: '' });
    },
    onError: () => { toast.error('Failed to add option'); },
  });

  const selectOption = useMutation(async (optId: number) => {
    await api.post(`/quotes/options/${optId}/select`);
  }, {
    onSuccess: () => { onRefresh(); qc.invalidateQueries('quotes'); },
  });

  const uploadMedia = useMutation(async ({ data, file_name, mime_type }: { data: string; file_name: string; mime_type: string }) => {
    await api.post(`/quotes/${quote.id}/media`, { data, file_name, mime_type });
  }, {
    onSuccess: () => { onRefresh(); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed to upload'); },
  });

  const deleteMedia = useMutation(async (id: number) => {
    await api.delete(`/quotes/media/${id}`);
  }, {
    onSuccess: () => { onRefresh(); },
    onError: () => { toast.error('Failed to delete photo'); },
  });

  // Photos are optional and can be added any time the bid is open. Each one
  // is shrunk on the phone first so a 5 MB camera shot doesn't sit in the DB.
  async function handlePhotoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    for (const raw of files) {
      let file = raw;
      try { file = await shrinkPhoto(raw); } catch { /* upload as-is */ }
      await new Promise<void>(resolve => {
        const reader = new FileReader();
        reader.onload = (ev) => { uploadMedia.mutate({ data: ev.target?.result as string, file_name: file.name, mime_type: file.type || 'image/jpeg' }); resolve(); };
        reader.onerror = () => resolve();
        reader.readAsDataURL(file);
      });
    }
  }

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    for (const file of picked) {
      if (file.size > 6 * 1024 * 1024) { toast.error(`${file.name} is over 6 MB`); continue; }
      const data = await new Promise<string>(resolve => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve(String(ev.target?.result || ''));
        reader.onerror = () => resolve('');
        reader.readAsDataURL(file);
      });
      if (!data) { toast.error(`Could not read ${file.name}`); continue; }
      uploadMedia.mutate({ data, file_name: file.name, mime_type: file.type || 'application/pdf' });
    }
  }

  const photos = media.filter((m: any) => String(m.mime_type || 'image/jpeg').startsWith('image/'));
  const files = media.filter((m: any) => !String(m.mime_type || 'image/jpeg').startsWith('image/'));

  function handleCatalogChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const id = e.target.value;
    const svc = catalog.find(s => String(s.id) === id);
    if (svc) {
      setItemForm(f => ({ ...f, service_catalog_id: id, description: svc.name, unit_price: String(svc.default_unit_price) }));
    } else {
      setItemForm(f => ({ ...f, service_catalog_id: '', description: '', unit_price: '' }));
    }
  }

  const { user: modalUser } = useAuth();
  const isBidder = modalUser?.role === 'bidder';
  const canEdit = quote?.status === 'draft' || quote?.status === 'sent';
  const canSend = quote?.status === 'draft' || quote?.status === 'sent';
  // Change orders (owner, Oct 6 2026: "if we are going to change a bid, but it's already accepted, click change order
  // so I can change something on the bid and then resend"). Once they have accepted, the bid's lines and options
  // change ONLY through a change order — the server refuses anything else — so the ticks that used to write straight
  // to an accepted bid are not offered on one. The button is the owner's alone ("only you" is the default he was offered).
  const acceptedBid = ['accepted', 'converted', 'completed'].includes(String(quote?.status || ''));
  const canChangeOrder = modalUser?.role === 'admin' && ['accepted', 'converted'].includes(String(quote?.status || ''));
  const openOrder = changeOrders?.open || null;
  const [showChangeOrder, setShowChangeOrder] = useState(false);
  const afterChangeOrder = () => { onRefresh(); qc.invalidateQueries('quotes'); qc.invalidateQueries('jobs'); };
  const changeOrderLabel = !openOrder ? 'Change order'
    : openOrder.status === 'sent' ? `Change order ${openOrder.number} — waiting on ${String(quote?.customer_name || '').trim().split(/\s+/)[0] || 'them'}`
    : openOrder.status === 'approved' ? `Change order ${openOrder.number} — approved, not on the calendar yet`
    : `Change order ${openOrder.number} — not sent yet`;
  const changeOrderHistory = (
    <ChangeOrderHistory quoteId={quote?.id} orders={changeOrders} canWrite={modalUser?.role === 'admin'} onOpen={() => setShowChangeOrder(true)} onChanged={afterChangeOrder} />
  );
  // Only what belongs on THIS bid (owner, Sept 15): a mowing bid gets the
  // route day and the lawn program; a treatment bid gets the lawn program;
  // an irrigation/sod/project bid gets neither, and no "mowing job" button.
  const titleText = String(quote?.title || '');
  const bidKind: 'mowing' | 'treatment' | 'project' =
    quote?.quote_kind === 'mowing' ? 'mowing'
    : quote?.quote_kind === 'treatment' ? 'treatment'
    : ['irrigation', 'cleanup', 'trimming', 'project', 'contract'].includes(String(quote?.quote_kind || '')) ? 'project'
    : quote?.service_frequency || /\bmow/i.test(titleText) ? 'mowing'
    : (quote?.package_key || /treatment|lawn program|program/i.test(titleText)) ? 'treatment'
    : 'project';
  // The bid's own lines — an option's lines are drawn inside its card (components/BidOptionBuilder). On a bid with
  // no options this is every line, so the list below is exactly what it always was.
  const baseLines = lineItems.filter((li: any) => !li.bid_option_id);
  // The lines are the truth for the total: if the stored total is stale or
  // zero while real lines exist, show what the lines add up to.
  const linesTotal = lineItems.reduce((sum: number, li: any) => sum + (li.is_selected ? (Number(li.total) || Number(li.quantity || 1) * Number(li.unit_price || 0)) : 0), 0)
    + groups.reduce((sum: number, g: any) => sum + (Number((g.options || []).find((o: any) => o.is_selected)?.price) || 0), 0);
  const displayTotal = Number(quote?.total) > 0 ? Number(quote.total) : linesTotal;
  const monthlyTotal = lineItems.reduce((sum: number, li: any) => sum + (li.is_selected && li.unit === 'month' ? (Number(li.total) || 0) : 0), 0);
  const visitTotal = lineItems.reduce((sum: number, li: any) => sum + (li.is_selected && li.unit === 'visit' ? (Number(li.total) || 0) : 0), 0);
  const oneTimeTotal = Math.max(0, displayTotal - monthlyTotal - visitTotal);
  // A bidder builds and sends quotes but doesn't convert them into scheduled work.
  // A draft converts too: making the job or the project is the acceptance (owner, Oct 8 2026).
  const canConvert = !isBidder && ['draft', 'sent', 'accepted'].includes(String(quote?.status || ''));
  // No bid is accepted unless the customer signed it (owner, Oct 20 2026): until then the accept and make-the-job
  // buttons are not offered — the one thing to do is send them the link. A bid already converted is past the lock.
  const unsigned = !!quote && !quote.agreement_signed_at && !['converted', 'declined', 'expired', 'completed'].includes(String(quote.status || ''));

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
            <div className="flex-1 min-w-0 pr-4">
              <h3 className="text-base font-black uppercase tracking-tight text-gray-900 truncate">{quote?.title}</h3>
              <p className="text-xs text-gray-500 mt-0.5">{quote?.customer_name}{quote?.address ? ` · ${quote.address.split(',')[0]}` : ''}</p>
              {openedWords(quote) && <p className={`text-xs mt-0.5 ${Number(quote?.view_count) > 0 ? 'text-blue-700 font-medium' : 'text-gray-500'}`}>{openedWords(quote)}</p>}
              {followup && (
                <p className="text-xs mt-0.5 text-gray-600">
                  {followup.words}
                  {followup.can_stop && (
                    <button type="button" onClick={() => followupSwitch.mutate()} disabled={followupSwitch.isLoading}
                      className="ml-2 font-semibold text-brand-700 underline disabled:opacity-50">
                      {followup.stopped ? 'Turn follow-ups back on' : 'Stop follow-ups'}
                    </button>
                  )}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[quote?.status] || 'bg-gray-100'}`}>
                {quote?.status}
              </span>
              <button onClick={onClose}><X className="h-5 w-5 text-gray-500 hover:text-gray-600" /></button>
            </div>
          </div>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
            <VisitHours
              value={{ visit_exact: quote.visit_exact, visit_earliest: quote.visit_earliest, visit_latest: quote.visit_latest }}
              saving={visitHours.isLoading}
              onSave={body => visitHours.mutate(body)}
            />
            {canEdit && (
              <button type="button" onClick={() => requireCard.mutate()} disabled={requireCard.isLoading}
                className={`w-full py-2.5 rounded-xl text-sm font-semibold border ${quote.require_card ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-900 border-gray-300'}`}>
                {requireCard.isLoading ? 'Saving…' : quote.require_card ? 'Require card on file ✓' : 'Require card on file'}
              </button>
            )}
            {!!quote.require_card && (
              <p className="text-xs text-gray-500 -mt-3">They put a card on file before they can accept. Nothing is charged.</p>
            )}
            {/* How they chose to be billed on the page (server: services/billCadence); changeable on the customer card. */}
            {!!quote.billing_choice && (
              <p className="text-xs text-gray-700 -mt-2">
                <span className="font-semibold">Billed {quote.billing_choice === 'per_visit' ? 'after each visit' : 'at month end'}</span>
                {' '}— their pick on the bid{['accepted', 'converted', 'completed'].includes(String(quote.status)) ? ', now on their customer card' : ''}.
              </p>
            )}
            {/* The site map on their bid — "where the work gets done" (owner, Oct 27 2026). Optional, owner only. */}
            {modalUser?.role === 'admin' && <BidSiteMapCard quote={quote} onRefresh={onRefresh} />}
            {/* Offer the lawn plans on the customer's page? Not on a lawn-plan bid (it IS the plans) or a business bid (never offered). */}
            {bidKind !== 'treatment' && !quote.company_name && ['draft', 'sent'].includes(String(quote.status)) && (
              <div>
                {canEdit ? (
                  <button type="button" onClick={() => offerPlansSwitch.mutate()} disabled={offerPlansSwitch.isLoading}
                    className={`w-full py-2.5 rounded-xl text-sm font-semibold border ${Number(quote.offer_plans ?? 1) !== 0 ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-900 border-gray-300'} disabled:opacity-80`}>
                    {offerPlansSwitch.isLoading ? 'Saving…' : Number(quote.offer_plans ?? 1) !== 0 ? 'Offer lawn plans on this bid ✓' : 'Offer lawn plans on this bid'}
                  </button>
                ) : null}
                <p className="text-xs text-gray-500 mt-2">{Number(quote.offer_plans ?? 1) !== 0
                  ? `Their page offers the three lawn plans, an upgrade, extra applications and the month the plan starts above Accept.${bidKind && bidKind !== 'mowing' ? ' On a project bid they show only when we already mow the yard.' : ''}`
                  : 'No lawn plans on their page — just the work on the bid. (The bed weed spray add-on still shows.)'}</p>
              </div>
            )}
            {/* Winter prices on this bid (services/winterWork; owner, Oct 29 2026): the one-time lines carry the Now / In winter
                choice by themselves; this takes it off the whole bid, or puts it back. The snowflake on a line does one line. */}
            {winterDeal?.on && winterDeal.offerable && ['draft', 'sent'].includes(String(quote.status)) && (
              <div>
                {canEdit ? (
                  <button type="button" onClick={() => winterBidSwitch.mutate(winterDeal.bid_on === false)} disabled={winterBidSwitch.isLoading}
                    className={`w-full py-2.5 rounded-xl text-sm font-semibold border ${winterDeal.bid_on !== false ? 'bg-sky-700 text-white border-sky-700' : 'bg-white text-gray-900 border-gray-300'} disabled:opacity-80`}>
                    {winterBidSwitch.isLoading ? 'Saving…' : winterDeal.bid_on !== false ? `Winter prices on this bid ✓ (${winterDeal.pct}% off Dec–Mar)` : 'Offer winter prices on this bid'}
                  </button>
                ) : null}
                <p className="text-xs text-gray-500 mt-2">{winterDeal.bid_on !== false
                  ? `Each one-time line shows "Do it now" or "In winter, ${winterDeal.pct}% off" and they choose line by line. The snowflake on a line takes that one line off.`
                  : 'Every line is done as quoted — no winter choice on their page.'}</p>
              </div>
            )}
            {/* The care plan this bid offers after the planting (services/bushFollowUp): when the job is finished the
                customer gets their watering page with these prices on it and can take one. Shown on a bid that reads
                like a planting job, or on any bid once prices are on it. */}
            {bushPlan && (bushPlan.looks_like_planting || bushPlan.offered.length > 0) && !['declined', 'expired', 'converted'].includes(String(quote.status)) && (
              <div>
                <button type="button" onClick={() => setCareOpen(o => !o)}
                  className={`w-full py-2.5 rounded-xl text-sm font-semibold border ${bushPlan.offered.length ? 'bg-emerald-700 text-white border-emerald-700' : 'bg-white text-gray-900 border-gray-300'}`}>
                  <Leaf className="inline h-4 w-4 -mt-0.5 mr-1" />
                  {bushPlan.offered.length ? `Plant care plan ✓ (${bushPlan.offered.map(o => `${fmtMoney(o.price)} / ${o.months} mo`).join(' · ')})` : 'Offer a plant care plan after this planting'}
                </button>
                {careOpen && (
                  <div className="mt-2 rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 space-y-2">
                    <p className="text-xs text-gray-700">What one care visit costs, at each rhythm. When this planting is marked complete the customer gets their watering and care page with these on it and can take one — nothing is charged, it lands on Needs Attention for you to book. Leave one empty to not offer it.</p>
                    {(bushPlan.months || [3, 4, 6]).map(m => (
                      <label key={m} className="flex items-center gap-2">
                        <span className="text-xs text-gray-800 w-28">Every {m} months</span>
                        <span className="text-xs text-gray-500">$</span>
                        <input type="number" step="0.01" min="0" className="input text-xs w-28" placeholder="a visit"
                          value={careValue(m)} onChange={e => setCarePrices(c => ({ ...c, [String(m)]: e.target.value }))} />
                      </label>
                    ))}
                    {canEdit && (
                      <button type="button" onClick={() => saveCarePlan.mutate()} disabled={saveCarePlan.isLoading}
                        className="btn-primary text-xs py-1 px-3">{saveCarePlan.isLoading ? 'Saving…' : 'Save the care plan'}</button>
                    )}
                    {!bushPlan.on && <p className="text-xs text-amber-700">The automatic care note is switched off, so nothing goes out yet — Settings → Texts &amp; email → "Care note after a new planting".</p>}
                  </div>
                )}
              </div>
            )}
            {/* Also after they accepted, for the owner (Oct 6 2026: "require 50% down real quick on alans bid"). */}
            {(canEdit || canChangeOrder) && (
              <button type="button" onClick={() => requireDeposit.mutate()} disabled={requireDeposit.isLoading || (!!deposit?.paid && !!Number(quote.require_deposit))}
                className={`w-full py-2.5 rounded-xl text-sm font-semibold border ${Number(quote.require_deposit) ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-900 border-gray-300'} disabled:opacity-80`}>
                {requireDeposit.isLoading ? 'Saving…' : Number(quote.require_deposit) ? 'Require 50% upfront ✓' : 'Require 50% upfront'}
              </button>
            )}
            {deposit && (Number(quote.require_deposit) === 1 || Number(quote.deposit_pct) > 0) && (
              <div className="-mt-3 text-xs space-y-1.5">
                {deposit.paid ? (
                  <p className="text-emerald-700 font-medium">
                    Deposit ${Number(deposit.paid_amount).toFixed(2)} paid{deposit.paid_at ? ` ${new Date(deposit.paid_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/Chicago' })}` : ''}{deposit.paid_method && deposit.paid_method !== 'stripe' ? ` (${deposit.paid_method})` : ' by card'} · {deposit.invoice_number}
                    {deposit.applied_to ? ` · taken off ${deposit.applied_to}` : ' · comes off the final bill'}
                  </p>
                ) : deposit.amount > 0 ? (
                  <p className="text-gray-500">
                    {!Number(quote.require_deposit)
                      ? `A ${deposit.pct}% deposit ($${Number(deposit.amount).toFixed(2)}) is written on this bid. It isn't collected by card — tap Require 50% upfront for that, or record it when you get it.`
                      : deposit.collectable
                      ? `They pay ${deposit.pct}% ($${Number(deposit.amount).toFixed(2)}) by card before they can accept. It comes off the final bill.`
                      : `${deposit.pct}% is $${Number(deposit.amount).toFixed(2)}. Card payments aren't live, so they can still accept — record the deposit when you get it.`}
                    {Number(deposit.base) !== Number(quote.total) && Number(deposit.base) > 0 ? ` (Taken on the one-time work, $${Number(deposit.base).toFixed(2)} — the mowing and its visit fees bill as usual.)` : ''}
                  </p>
                ) : (
                  <p className="text-gray-500">No one-time work on this bid, so there's nothing to take upfront.</p>
                )}
                {!deposit.paid && deposit.amount > 0 && modalUser?.role === 'admin' && (
                  depositForm.open ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <select aria-label="How they paid" value={depositForm.method} onChange={e => setDepositForm(f => ({ ...f, method: e.target.value }))} className="input text-xs py-1.5 w-auto">
                        <option value="cash">Cash</option><option value="check">Check</option><option value="zelle">Zelle</option><option value="other">Other</option>
                      </select>
                      <input aria-label="Amount received" type="number" inputMode="decimal" min="0" step="0.01" placeholder={Number(deposit.amount).toFixed(2)}
                        value={depositForm.amount} onChange={e => setDepositForm(f => ({ ...f, amount: e.target.value }))} className="input text-xs py-1.5 w-28" />
                      <button type="button" onClick={() => depositReceived.mutate()} disabled={depositReceived.isLoading} className="btn-primary text-xs py-1.5">
                        {depositReceived.isLoading ? 'Saving…' : 'Record deposit'}
                      </button>
                      <button type="button" onClick={() => setDepositForm({ open: false, method: 'cash', amount: '' })} className="text-gray-500 underline">Cancel</button>
                    </div>
                  ) : (
                    <button type="button" onClick={() => setDepositForm(f => ({ ...f, open: true }))} className="text-brand-700 font-semibold underline">Deposit received another way</button>
                  )
                )}
              </div>
            )}
            {/* A mowing bid at a glance — the numbers and the Send button first; the wording is below, folded (owner, Sep 18 2026: "bidding for a mowing job is still janky"). */}
            {bidKind === 'mowing' && (() => {
              const mow = lineItems.find((li: any) => li.is_selected && li.unit === 'visit' && !li.is_travel && !li.addon_key && !li.plan_key && !li.offer_key);
              const travel = lineItems.find((li: any) => li.is_selected && li.is_travel);
              const plan = lineItems.find((li: any) => li.is_selected && li.plan_key);
              const addons = lineItems.filter((li: any) => li.is_selected && li.addon_key);
              const freq = String(quote?.service_frequency || '').replace('_', ' ');
              return (
                <div className="rounded-xl border border-brand-200 bg-brand-50/40 p-3 space-y-2">
                  <div className="flex items-baseline justify-between gap-2">
                    <p className="text-2xl font-bold text-gray-900">{mow ? fmtMoney(mow.unit_price) : fmtMoney(visitTotal)}<span className="text-sm font-medium text-gray-500">/mow{freq ? ` · ${freq}` : ''}</span></p>
                    {travel && <p className="text-sm text-amber-800">+ {fmtMoney(travel.unit_price)} travel a mow</p>}
                  </div>
                  {(() => { const og = lineItems.filter((li: any) => li.is_selected && !li.unit && !li.is_optional && /overgrow|catch-?up|first cut|tall grass/i.test(String(li.description))).reduce((s: number, li: any) => s + (Number(li.unit_price) || 0), 0);
                    return og > 0 ? <p className="text-sm font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">Overgrown: first visit {fmtMoney((mow ? Number(mow.unit_price) : visitTotal) + og)} (includes a one-time {fmtMoney(og)} catch-up)</p> : null; })()}
                  <FirstVisitEdit quote={quote} canEdit={canEdit || (quote?.status === 'accepted' && !schedule)} onRefresh={onRefresh} />
                  {(plan || addons.length > 0) && (
                    <p className="text-xs text-gray-700">
                      {plan ? `${plan.description} — ${fmtMoney(plan.unit_price)}/mo` : ''}{plan && addons.length ? ' · ' : ''}{addons.length ? `${addons.length} add-on${addons.length === 1 ? '' : 's'}: ${addons.map((a: any) => a.description).join(', ')}` : ''}
                    </p>
                  )}
                  {!mow && lineItems.length === 0 && <p className="text-xs text-amber-800">No mowing price on this bid yet — add the line below.</p>}
                  {/* In the server's words — "every other Monday from Mon Oct 12" — never a bare "Mondays from 2026-10-12". */}
                  {quote?.status === 'accepted' && schedule && <p className="text-sm font-semibold text-emerald-800">✓ On the schedule — {schedule.words || `${schedule.day}s`}</p>}
                  {quote?.status === 'accepted' && !schedule && oneTimeMow && <p className="text-sm font-semibold text-emerald-800">✓ One mow booked — {oneTimeMow.date_words}{oneTimeMow.route_name ? ` on ${oneTimeMow.route_name}` : ' — no route is out that day, put someone on it'}</p>}
                  {quote?.status === 'accepted' && !schedule && !oneTimeMow && (
                    <button onClick={() => putOnSchedule.mutate()} disabled={putOnSchedule.isLoading} className="w-full py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold disabled:opacity-50">{putOnSchedule.isLoading ? 'Scheduling…' : 'Put it on the schedule'}</button>
                  )}
                  <div className="flex gap-2">
                    {canSend && (
                      <button onClick={() => setShowSend(true)} className="press flex-1 py-2.5 rounded-xl bg-brand-600 text-black text-sm font-semibold flex items-center justify-center gap-1.5">
                        <Share2 className="h-4 w-4" /> Send by text
                      </button>
                    )}
                    {quote?.id && (
                      <button onClick={copyLink} className="px-3 py-2.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-700 flex items-center gap-1.5">
                        {linkCopied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />} {linkCopied ? 'Copied' : 'Link'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })()}
            {/* Any other bid: after a yes, the one next step is the job */}
            {bidKind !== 'mowing' && (quote?.status === 'accepted' || quote?.status === 'converted') && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-3 space-y-2">
                {convertedJob ? (
                  <p className="text-sm font-semibold text-emerald-800">✓ Job #{convertedJob.id} · {new Date(`${String(convertedJob.scheduled_date).slice(0, 10)}T12:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}{convertedJob.time_start ? ` at ${convertedJob.time_start}` : ''} · {convertedJob.status} <button onClick={() => { onClose(); navigate(`/jobs/${convertedJob.id}`); }} className="underline font-normal">open</button></p>
                ) : convertedProject && (convertedProject.job_id || convertedProject.own) ? (
                  // The job cannot take this project's place (it has a bill, people, photos or its own prices), or a
                  // change order already put the work on a visit: say where the work is instead of offering a button
                  // the server would refuse.
                  <p className="text-sm font-semibold text-emerald-800">
                    {convertedProject.job_id
                      ? <>✓ On the calendar — <Link to={`/jobs/${convertedProject.job_id}`} className="underline">job #{convertedProject.job_id}</Link></>
                      : <>Project #{convertedProject.id} {projectOwnWords(convertedProject.own)} — <Link to={`/projects?open=${convertedProject.id}`} className="underline">open it</Link></>}
                  </p>
                ) : (
                  <>
                    {/* Project Work lives on the Projects page only — not the calendar, not the crew's day. The job is what
                        puts it on a day, and it takes the project's place (owner, Oct 8 2026: this button answered
                        "This bid is already project #7 — nothing more to make from it."). */}
                    <p className="text-sm font-semibold text-emerald-800">
                      {convertedProject && !convertedProject.gone
                        ? `Project #${convertedProject.id} is not on the calendar yet — make the job to put it on a day. The job takes the project's place.`
                        : 'Accepted — make it a job'}
                    </p>
                    <button onClick={() => setShowConvert(true)} className="w-full py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold">Make the job (pick the day)</button>
                  </>
                )}
              </div>
            )}
            {/* Every bid has a start date, not only mowing (owner, Oct 6 2026: "there is no start date for bids that aren't mowing"). */}
            {bidKind !== 'mowing' && (
              <div className="rounded-xl border border-gray-200 bg-white p-3">
                <FirstVisitEdit quote={quote} word="Start date" canEdit={canEdit || (quote?.status === 'accepted' && !convertedJob)} onRefresh={onRefresh} />
              </div>
            )}
            {bidKind !== 'mowing' && <IntroEditor quote={quote} canEdit={canEdit} onRefresh={() => { onRefresh(); qc.invalidateQueries('quotes'); }} />}

            {/* Photos */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Photos</p>
                {canEdit && (
                  <span className="flex items-center gap-3">
                    <label className="text-xs text-brand-600 hover:text-brand-700 font-medium flex items-center gap-1 cursor-pointer">
                      <Camera className="h-3 w-3" />
                      {uploadMedia.isLoading ? 'Uploading…' : 'Take photo'}
                      <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handlePhotoUpload} />
                    </label>
                    <label className="text-xs text-brand-600 hover:text-brand-700 font-medium flex items-center gap-1 cursor-pointer">
                      <UploadCloud className="h-3 w-3" /> Upload
                      <input type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoUpload} />
                    </label>
                  </span>
                )}
              </div>
              {photos.length > 0 ? (
                <div className="grid grid-cols-3 gap-2">
                  {photos.map((m: any) => (
                    <div key={m.id} className="relative group">
                      <img src={m.data} alt={m.file_name || 'Photo'} className="w-full h-20 object-cover rounded-lg" />
                      {canEdit && (
                        <button onClick={() => deleteMedia.mutate(m.id)}
                          className="absolute top-1 right-1 bg-black/60 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          <X className="h-3 w-3 text-snow" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-500 italic">No photos — that's fine, add some later if you want.</p>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Files</p>
                {canEdit && (
                  <label className="text-xs text-brand-600 hover:text-brand-700 font-medium flex items-center gap-1 cursor-pointer">
                    <FileText className="h-3 w-3" />
                    {uploadMedia.isLoading ? 'Uploading…' : 'Upload PDF'}
                    <input type="file" accept="application/pdf,.pdf,.doc,.docx,.xls,.xlsx,.csv,text/plain,text/csv" multiple className="hidden" onChange={handleFileUpload} />
                  </label>
                )}
              </div>
              {files.length > 0 ? (
                <ul className="space-y-1">
                  {files.map((m: any) => (
                    <li key={m.id} className="flex items-center gap-2 text-sm">
                      <a href={m.data} download={m.file_name || 'file'} target="_blank" rel="noreferrer" className="flex-1 truncate text-brand-700 font-medium">{m.file_name || 'File'}</a>
                      {canEdit && <button onClick={() => deleteMedia.mutate(m.id)} className="text-gray-400 hover:text-red-600"><X className="h-3.5 w-3.5" /></button>}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-gray-500 italic">Plans, proposals, PDFs. The customer can open them on the bid.</p>
              )}
            </div>

            {/* Change orders, one line each. They belong under "Signed by" (below); on a mowing bid that wording is folded
                shut, so here they sit just above the fold where he will see them. */}
            {bidKind === 'mowing' && changeOrderHistory}
            {/* Scope of Work & Agreement — folded on a mowing bid; the numbers above are what the bidder needs on the phone */}
            <details open={bidKind !== 'mowing'} className="group">
              <summary className="text-xs font-semibold text-gray-500 uppercase tracking-wide cursor-pointer list-none flex items-center gap-1 mb-2">
                <span className="group-open:hidden">▸</span><span className="hidden group-open:inline">▾</span> Wording the customer sees
              </summary>
              {bidKind === 'mowing' && <IntroEditor quote={quote} canEdit={canEdit} onRefresh={() => { onRefresh(); qc.invalidateQueries('quotes'); }} />}
            {/* Non-mowing bids: AI writes the job scope from the lines; the agreement below is built from it. */}
            <BidScopeEditor quote={quote} onSaved={() => { onRefresh(); qc.invalidateQueries('quotes'); }} />
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Scope of Work &amp; Agreement</p>
                {/* Once they have signed it, the agreement is what they signed: no redraft, no edit (change orders, Oct 6 2026). */}
                {!quote.agreement_signed_at && (
                <div className="flex items-center gap-3">
                  <button onClick={generateScope} disabled={scopeBusy}
                    className="text-xs text-purple-600 hover:text-purple-800 font-medium disabled:opacity-40">
                    {scopeBusy ? 'Drafting…' : quote.scope_of_work ? '✨ Redraft' : '✨ Generate'}
                  </button>
                  {quote.scope_of_work && !scopeEditing && (
                    <button onClick={() => { setScopeDraft(quote.scope_of_work); setScopeEditing(true); }}
                      className="text-xs text-brand-600 hover:text-brand-700 font-medium">Edit</button>
                  )}
                </div>
                )}
              </div>
              {quote.agreement_signed_at && (
                <p className="mb-2 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-lg px-2.5 py-1.5">
                  ✍️ Signed by {quote.agreement_name} on {new Date(String(quote.agreement_signed_at).replace(' ', 'T') + (String(quote.agreement_signed_at).includes('Z') ? '' : 'Z')).toLocaleString()}{(quote as any).agreement_verified_via === 'text' ? ' — by text (their yes on Texts)' : ''}
                </p>
              )}
              {bidKind !== 'mowing' && changeOrderHistory}
              {scopeEditing && !quote.agreement_signed_at ? (
                <div className="space-y-2">
                  <textarea value={scopeDraft} onChange={e => setScopeDraft(e.target.value)} rows={10}
                    className="input text-xs w-full font-mono" />
                  <div className="flex gap-2">
                    <button onClick={saveScope} className="btn-primary text-xs py-1 px-3">Save</button>
                    <button onClick={() => setScopeEditing(false)} className="btn-secondary text-xs py-1 px-3">Cancel</button>
                  </div>
                </div>
              ) : quote.scope_of_work ? (
                <pre className="text-xs text-gray-600 whitespace-pre-wrap font-sans bg-gray-50 rounded-lg p-3 max-h-48 overflow-y-auto">{quote.scope_of_work}</pre>
              ) : (
                <p className="text-xs text-gray-500 italic">No scope yet — Generate drafts it from the line items; the customer signs it when accepting the quote.</p>
              )}
            </div>

            </details>
            {/* Checklist — only shown when this bid already has one */}
            {checklist.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Checklist</p>
              {checklist.length > 0 && (
                <ul className="space-y-1 mb-2">
                  {checklist.map((it: any) => (
                    <li key={it.id} className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={!!it.is_done} onChange={() => toggleCheck(it)}
                        className="rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
                      <span className={it.is_done ? 'line-through text-gray-500 flex-1' : 'text-gray-800 flex-1'}>{it.label}</span>
                      <button onClick={() => delCheck(it)} className="text-gray-300 hover:text-red-500 text-xs">✕</button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex gap-2">
                <input value={newCheck} onChange={e => setNewCheck(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCheck(); } }}
                  placeholder="Add a checklist item…"
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400" />
                <button onClick={addCheck} disabled={!newCheck.trim()}
                  className="px-3 py-1.5 text-sm font-medium text-brand-700 border border-brand-200 rounded-lg hover:bg-brand-50 disabled:opacity-50">Add</button>
              </div>
            </div>
            )}

            {/* A lawn plan can ride any bid (owner, Oct 6 2026: "allow me to add treatment plans to any bid"); the route day stays mowing-only. */}
            <div className="py-3">
              <BidPlanPicker quoteId={quote.id} propertyId={quote.property_id} canEdit={canEdit} onRefresh={onRefresh} />
            </div>
            {bidKind === 'mowing' && <ScheduleRow quote={quote} canEdit={canEdit} onRefresh={onRefresh} />}
            {bidKind === 'mowing' && <RouteDayPicker quote={quote} canEdit={canEdit} onRefresh={onRefresh} />}
            <ReferralRow quote={quote} canEdit={canEdit} onRefresh={onRefresh} />

            {/* Line items */}
            <div>
              {/* Several options on one bid (components/BidOptionBuilder): each its own title, photos and priced
                  lines. Above the bid's own lines, because an option is read first. A bid with none draws one button. */}
              <BidOptionBuilder quoteId={quote.id} options={bidOptions} canEdit={canEdit && !acceptedBid} onRefresh={onRefresh} />
              <div className="flex items-center justify-between mb-2 mt-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {bidOptions.length ? 'Line items — on the bid itself' : 'Line Items'}
                </p>
                <div className="flex items-center gap-3">
                  {canEdit && (
                    <button onClick={() => setAiOpen(v => !v)}
                      className="text-xs text-purple-600 hover:text-purple-800 font-medium flex items-center gap-1">
                      ✨ AI Quote
                    </button>
                  )}
                  {canEdit && !addingItem && (
                    <button onClick={() => setAddingItem(true)}
                      className="text-xs text-brand-600 hover:text-brand-700 font-medium flex items-center gap-1">
                      <Plus className="h-3 w-3" /> Add
                    </button>
                  )}
                </div>
              </div>
              {aiOpen && canEdit && (
                <div className="mb-3 p-2.5 border border-purple-200 rounded-xl bg-purple-50/50">
                  <p className="text-[11px] text-purple-700 font-medium mb-1.5">
                    Paste raw notes — AI builds the itemized quote. Either/or repairs and upgrades come in as optional lines.
                  </p>
                  <textarea value={aiText} onChange={e => setAiText(e.target.value)} rows={4}
                    placeholder="station 5 hunter valve low pressure — diaphragm 95 or whole valve 185…"
                    className="input text-sm w-full resize-none" />
                  <div className="flex gap-2 mt-1.5">
                    <button onClick={buildQuoteWithAi} disabled={!aiText.trim() || aiBusy}
                      className="text-xs px-3 py-1.5 rounded-lg bg-purple-600 text-white font-semibold hover:bg-purple-700 disabled:opacity-40 flex items-center gap-1.5">
                      {aiBusy ? <><span className="animate-spin rounded-full h-3 w-3 border-b-2 border-white inline-block" /> Building… (~30s)</> : 'Build quote lines'}
                    </button>
                    <button onClick={() => { setAiOpen(false); setAiText(''); }}
                      className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-100">Cancel</button>
                  </div>
                </div>
              )}
              <div className="space-y-1">
                {baseLines.map((li: any) => (
                  editingItem?.id === li.id ? (
                    <div key={li.id} className="bg-brand-50 rounded-lg p-3 space-y-2">
                      <input value={editingItem.description}
                        onChange={e => setEditingItem((x: any) => ({ ...x, description: e.target.value }))}
                        className="input text-sm w-full" />
                      <div className="flex gap-2">
                        <input type="number" value={editingItem.quantity}
                          onChange={e => setEditingItem((x: any) => ({ ...x, quantity: e.target.value }))}
                          className="input text-sm w-20" placeholder="Qty" />
                        <select value={editingItem.unit || ''} onChange={e => setEditingItem({ ...editingItem, unit: e.target.value })} className="input text-sm w-28" title="Priced per">
                          <option value="">one-time</option><option value="month">per month</option><option value="visit">per visit</option><option value="year">per year</option>
                        </select>
                        <input type="number" value={editingItem.quote_on_request ? '' : editingItem.unit_price} disabled={!!editingItem.quote_on_request}
                          onChange={e => setEditingItem((x: any) => ({ ...x, unit_price: e.target.value }))}
                          className="input text-sm flex-1" placeholder={editingItem.quote_on_request ? 'Quote on request' : 'Unit price'} step="0.01" />
                      </div>
                      {editingItem.unit === 'month' && !editingItem.plan_key && !editingItem.addon_key && !editingItem.offer_key && (
                        <RhythmFields n={editingItem.every_n == null ? '' : String(editingItem.every_n)} unit={(editingItem.every_unit || 'months') as EveryUnit} firstOn={editingItem.first_on ? String(editingItem.first_on).slice(0, 10) : ''}
                          onChange={patch => setEditingItem((x: any) => ({ ...x, ...patch }))} />
                      )}
                      <label className="flex items-center gap-2 text-xs text-gray-600">
                        <input type="checkbox" checked={editingItem.sub_cost != null}
                          onChange={e => setEditingItem((x: any) => ({ ...x, sub_cost: e.target.checked ? '' : null, markup_pct: e.target.checked ? (x.markup_pct ?? lastMarkup()) : null }))}
                          className="rounded text-brand-600" />
                        Subcontracted
                      </label>
                      {editingItem.sub_cost != null && (
                        <SubFields cost={String(editingItem.sub_cost ?? '')} pct={String(editingItem.markup_pct ?? '')}
                          onChange={(cost, pct) => { rememberMarkup(pct); setEditingItem((x: any) => ({ ...x, sub_cost: cost, markup_pct: pct, unit_price: markedUp(cost, pct) || x.unit_price })); }} />
                      )}
                      <label className="flex items-center gap-2 text-xs text-gray-600">
                        <input type="checkbox" checked={editingItem.is_optional}
                          onChange={e => setEditingItem((x: any) => ({ ...x, is_optional: e.target.checked }))}
                          className="rounded text-brand-600" />
                        Optional line item
                      </label>
                      <label className="flex items-center gap-2 text-xs text-gray-600">
                        <input type="checkbox" checked={!!editingItem.quote_on_request}
                          onChange={e => setEditingItem((x: any) => ({ ...x, quote_on_request: e.target.checked, is_optional: e.target.checked ? true : x.is_optional }))}
                          className="rounded text-brand-600" />
                        Quote on request — no price yet; they tick it to ask for one
                      </label>
                      <div className="flex gap-2">
                        <button onClick={() => updateItem.mutate(editingItem)} disabled={updateItem.isLoading || (editingItem.unit === 'month' && !editingItem.plan_key && !editingItem.addon_key && !editingItem.offer_key && !editingItem.quote_on_request && !(Number(editingItem.every_n) >= 1))}
                          className="btn-primary text-xs py-1 px-3">Save</button>
                        <button onClick={() => setEditingItem(null)} className="btn-secondary text-xs py-1 px-3">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div key={li.id} className="space-y-2">
                    <div className={`flex items-center justify-between rounded-lg px-3 py-2 group ${li.is_optional ? 'bg-blue-50 border border-blue-100' : 'bg-gray-50'}`}>
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {li.is_optional && !acceptedBid && (
                          <button onClick={() => toggleItemSelected.mutate({ id: li.id, is_selected: !li.is_selected })} className="flex-shrink-0">
                            {li.is_selected
                              ? <ToggleRight className="h-4 w-4 text-brand-600" />
                              : <ToggleLeft className="h-4 w-4 text-gray-500" />}
                          </button>
                        )}
                        <div className={`flex-1 min-w-0 ${li.is_optional && !li.is_selected ? 'opacity-50' : ''}`}>
                          <p className="text-sm text-gray-800 truncate">{li.description}</p>
                          {li.quantity !== 1 && !Number(li.quote_on_request) && <p className="text-xs text-gray-500">{li.quantity} × {fmtMoney(li.unit_price)}</p>}
                          {li.sub_cost != null && <p className="text-[11px] text-gray-500">Sub · cost {fmtMoney(li.sub_cost)} + {Number(li.markup_pct) || 0}%{li.quantity !== 1 ? ' each' : ''}</p>}
                          {li.unit === 'month' && li.every_n && <p className="text-[11px] text-gray-500">Billed monthly · done {rhythmWords(li.every_n, li.every_unit)}{li.first_on ? ` · first ${String(li.first_on).slice(0, 10)}` : ''}</p>}
                          {li.unit === 'month' && !li.every_n && !li.plan_key && !li.addon_key && !li.offer_key && Number(li.unit_price) > 0 && <p className="text-[11px] text-amber-700">Billed monthly — how often is it done? Edit the line and pick a rhythm.</p>}
                          {/* The sizes this line is offered in (services/bushSizes): what the customer picks from. */}
                          {sizesOf(li) && (
                            <p className="text-[11px] text-emerald-700 font-medium">
                              {sizesOf(li)!.options.length} size{sizesOf(li)!.options.length === 1 ? '' : 's'} offered · {sizesOf(li)!.options.map(o => `${o.label} ${fmtMoney(o.price)}`).join(' · ')} — showing {sizesOf(li)!.options.find(o => o.key === sizesOf(li)!.chosen)?.label || sizesOf(li)!.options[0]?.label}
                            </p>
                          )}
                          {/* The winter offer on this line (services/winterWork): what they pay if they wait, or what they chose. */}
                          {Number(li.winter) === 1
                            ? <p className="text-[11px] text-sky-700 font-medium">Chosen for winter — {Number(li.winter_pct) || winterDeal?.pct || 10}% off{li.winter_full_price != null ? ` (was ${fmtMoney(Number(li.winter_full_price) * (Number(li.quantity) || 1))})` : ''}</p>
                            : winterOffered(li) && <p className="text-[11px] text-sky-700 font-medium">Winter offer · {fmtMoney(winterPriceOf(li))} if they wait ({winterDeal?.pct ?? 10}% off) — they choose on the bid{li.winter_offer == null ? ' · automatic' : ''}</p>}
                        </div>
                        {li.is_optional && <span className="text-[10px] text-blue-500 font-medium flex-shrink-0">{Number(li.quote_on_request) ? 'quote on request' : 'optional'}</span>}
                        {canEdit && !acceptedBid && canSize(li) && (
                          <button type="button" onClick={() => setSizingLine(sizingLine === li.id ? null : li.id)}
                            title={sizesOf(li) ? 'Sizes offered on this line — tap to change them' : 'Offer this line in sizes (1, 3, 5, 7, 15 gallon…) with a price each'}
                            aria-pressed={!!sizesOf(li)}
                            className={`flex-shrink-0 p-1 rounded-md border ${sizesOf(li) ? 'bg-emerald-100 border-emerald-300 text-emerald-700' : 'border-gray-200 text-gray-400 hover:text-emerald-600 hover:border-emerald-300'}`}>
                            <Leaf className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {canEdit && !acceptedBid && winterDeal?.on && winterDeal.offerable && winterDeal.bid_on !== false && Number(li.winter) !== 1 && winterDeal.can_offer.includes(li.id) && (
                          <button type="button" onClick={() => toggleWinterOffer.mutate({ id: li.id, on: !winterOffered(li) })} disabled={toggleWinterOffer.isLoading}
                            title={winterOffered(li) ? 'Offered in winter — tap to take the offer off this line' : `Offer it in winter, ${winterDeal.pct}% off`}
                            aria-pressed={winterOffered(li)}
                            className={`flex-shrink-0 p-1 rounded-md border ${winterOffered(li) ? 'bg-sky-100 border-sky-300 text-sky-700' : 'border-gray-200 text-gray-400 hover:text-sky-600 hover:border-sky-300'}`}>
                            <Snowflake className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                      <div className="flex items-center gap-1 ml-2">
                        <span className={`text-sm font-semibold ml-2 ${li.is_optional && !li.is_selected ? 'text-gray-300 line-through' : 'text-gray-900'}`}>
                          {Number(li.quote_on_request) ? <span className="text-xs font-medium text-gray-500">Quote on request</span> : fmtMoney(li.total)}{li.unit === 'month' ? <span className="text-xs font-normal text-gray-500">/mo</span> : li.unit === 'visit' ? <span className="text-xs font-normal text-gray-500">/visit</span> : li.unit === 'year' ? <span className="text-xs font-normal text-gray-500">/yr</span> : null}
                        </span>
                        {canEdit && (
                          <div className="flex opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => setEditingItem({ ...li })}
                              className="p-1 text-gray-500 hover:text-brand-600 rounded"><Edit2 className="h-3.5 w-3.5" /></button>
                            <button onClick={() => { if (window.confirm('Remove this item?')) deleteItem.mutate(li.id); }}
                              className="p-1 text-gray-500 hover:text-red-500 rounded"><Trash2 className="h-3.5 w-3.5" /></button>
                          </div>
                        )}
                      </div>
                    </div>
                    {/* The sizes this line is offered in, each with its price (components/BushSizeEditor). */}
                    {sizingLine === li.id && sizesDeal && (
                      <BushSizeEditor catalog={sizesDeal.catalog} options={sizesOf(li)?.options || []} chosen={sizesOf(li)?.chosen || null}
                        quantity={Number(li.quantity) || 1} busy={saveSizes.isLoading}
                        onSave={(sizes, keep) => saveSizes.mutate({ id: li.id, sizes, keep })}
                        onCancel={() => setSizingLine(null)} />
                    )}
                    </div>
                  )
                ))}

                {addingItem && (
                  <div className="bg-brand-50 rounded-lg p-3 space-y-2">
                    <select onChange={handleCatalogChange} value={itemForm.service_catalog_id} className="input text-sm w-full">
                      <option value="">— Pick from catalog (optional) —</option>
                      {catalog.map((s: any) => <option key={s.id} value={s.id}>{s.name} · ${s.default_unit_price}/{s.unit}</option>)}
                    </select>
                    <input value={itemForm.description} onChange={e => setItemForm(f => ({ ...f, description: e.target.value }))}
                      className="input text-sm w-full" placeholder="Description *" />
                    <div className="flex gap-2">
                      <input type="number" value={itemForm.quantity} onChange={e => setItemForm(f => ({ ...f, quantity: e.target.value }))}
                        className="input text-sm w-20" placeholder="Qty" />
                      <select value={itemForm.unit} onChange={e => setItemForm(f => ({ ...f, unit: e.target.value }))} className="input text-sm w-28" title="Priced per">
                        <option value="">one-time</option><option value="month">per month</option><option value="visit">per visit</option><option value="year">per year</option>
                      </select>
                      <input type="number" value={itemForm.on_request ? '' : itemForm.unit_price} disabled={itemForm.on_request} onChange={e => setItemForm(f => ({ ...f, unit_price: e.target.value }))}
                        className="input text-sm flex-1" placeholder={itemForm.on_request ? 'Quote on request' : 'Unit price *'} step="0.01" />
                      <span className="text-xs text-gray-500 self-center whitespace-nowrap">
                        = {fmtMoney(Number(itemForm.quantity || 1) * Number(itemForm.unit_price || 0))}
                      </span>
                    </div>
                    {itemForm.unit === 'month' && <RhythmFields n={itemForm.every_n} unit={itemForm.every_unit} firstOn={itemForm.first_on} onChange={patch => setItemForm(f => ({ ...f, ...patch }))} />}
                    <label className="flex items-center gap-2 text-xs text-gray-600">
                      <input type="checkbox" checked={itemForm.sub}
                        onChange={e => setItemForm(f => ({ ...f, sub: e.target.checked, pct: e.target.checked ? (f.pct || lastMarkup()) : '', cost: e.target.checked ? f.cost : '' }))}
                        className="rounded text-brand-600" />
                      Subcontracted — price it from the sub's cost + a markup
                    </label>
                    {itemForm.sub && (
                      <SubFields cost={itemForm.cost} pct={itemForm.pct}
                        onChange={(cost, pct) => { rememberMarkup(pct); setItemForm(f => ({ ...f, cost, pct, unit_price: markedUp(cost, pct) || f.unit_price })); }} />
                    )}
                    <label className="flex items-center gap-2 text-xs text-gray-600">
                      <input type="checkbox" checked={itemForm.is_optional}
                        onChange={e => setItemForm(f => ({ ...f, is_optional: e.target.checked }))}
                        className="rounded text-brand-600" />
                      Optional (customer can select/deselect)
                    </label>
                    <label className="flex items-center gap-2 text-xs text-gray-600">
                      <input type="checkbox" checked={itemForm.on_request}
                        onChange={e => setItemForm(f => ({ ...f, on_request: e.target.checked, is_optional: e.target.checked ? true : f.is_optional }))}
                        className="rounded text-brand-600" />
                      Quote on request — no price yet; they tick it to ask for one
                    </label>
                    <div className="flex gap-2">
                      <button onClick={() => addItem.mutate()} disabled={addItem.isLoading || !itemForm.description || (!itemForm.unit_price && !itemForm.on_request) || (itemForm.unit === 'month' && !itemForm.on_request && !(Number(itemForm.every_n) >= 1))}
                        className="btn-primary text-xs py-1 px-3">Add</button>
                      <button onClick={() => setAddingItem(false)} className="btn-secondary text-xs py-1 px-3">Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Option groups */}
            {groups.map((g: any) => (
              <div key={g.id} className="border border-gray-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-gray-800">{g.name} <span className="text-xs text-gray-500 font-normal">(choose one)</span></p>
                  <div className="flex gap-1">
                    {canEdit && <button onClick={() => setAddingOption(g.id)}
                      className="text-xs text-brand-600 hover:text-brand-700 font-medium px-1.5"><Plus className="h-3.5 w-3.5" /></button>}
                    {canEdit && <button onClick={() => { if (window.confirm(`Delete group "${g.name}"?`)) deleteGroup.mutate(g.id); }}
                      className="p-1 text-gray-500 hover:text-red-500 rounded"><Trash2 className="h-3.5 w-3.5" /></button>}
                  </div>
                </div>
                <div className="space-y-1">
                  {(g.options || []).map((opt: any) => (
                    <button key={opt.id} onClick={() => selectOption.mutate(opt.id)} disabled={acceptedBid}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border text-left transition-colors ${opt.is_selected ? 'border-brand-400 bg-brand-50 text-brand-800' : `border-gray-200 bg-white text-gray-700 ${acceptedBid ? 'cursor-default' : 'hover:border-brand-300'}`}`}>
                      <div className="flex items-center gap-2">
                        <div className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${opt.is_selected ? 'border-brand-500 bg-brand-500' : 'border-gray-400'}`}>
                          {opt.is_selected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                        </div>
                        <span className="text-sm">{opt.name}</span>
                        {opt.description && <span className="text-xs text-gray-500">{opt.description}</span>}
                      </div>
                      {opt.price > 0 && <span className="text-sm font-medium text-gray-700 ml-2">+{fmtMoney(opt.price)}</span>}
                    </button>
                  ))}
                  {g.options?.length === 0 && <p className="text-xs text-gray-500 italic">No options yet.</p>}
                  {addingOption === g.id && (
                    <div className="bg-brand-50 rounded-lg p-2 space-y-2 mt-1">
                      <input value={optionForm.name} onChange={e => setOptionForm(f => ({ ...f, name: e.target.value }))}
                        className="input text-sm w-full" placeholder="Option name" />
                      <div className="flex gap-2">
                        <input value={optionForm.description} onChange={e => setOptionForm(f => ({ ...f, description: e.target.value }))}
                          className="input text-sm flex-1" placeholder="Description (optional)" />
                        <input type="number" value={optionForm.price} onChange={e => setOptionForm(f => ({ ...f, price: e.target.value }))}
                          className="input text-sm w-24" placeholder="Add'l price" step="0.01" />
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => addOption.mutate()} disabled={!optionForm.name || addOption.isLoading}
                          className="btn-primary text-xs py-1 px-3">Add Option</button>
                        <button onClick={() => setAddingOption(null)} className="btn-secondary text-xs py-1 px-3">Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Add option group */}
            {canEdit && (
              <div>
                {addingGroup ? (
                  <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                    <input value={groupName} onChange={e => setGroupName(e.target.value)} className="input text-sm w-full"
                      placeholder="Group name (e.g. Mulch color)" />
                    <div className="flex gap-2">
                      <button onClick={() => addGroup.mutate()} disabled={!groupName || addGroup.isLoading}
                        className="btn-primary text-xs py-1 px-3">Add Group</button>
                      <button onClick={() => setAddingGroup(false)} className="btn-secondary text-xs py-1 px-3">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => setAddingGroup(true)}
                    className="text-xs text-gray-500 hover:text-gray-700 font-medium flex items-center gap-1 mt-1">
                    <Plus className="h-3 w-3" /> Add option group (single-select)
                  </button>
                )}
              </div>
            )}

            {/* Total */}
            <div className="border-t border-gray-200 pt-3">
              <div className="flex items-center justify-between">
                <span className="text-base font-bold text-gray-900">Total</span>
                <span className="text-xl font-bold text-green-700">
                  {/* A monthly-contract bid (services/contractBid): the month with the picks, the term, the tax on each bill. */}
                  {quote?.quote_kind === 'contract'
                    ? `${fmtMoney(displayTotal)}/mo · ${Number(quote.contract_months) || 12} months${Number(quote.tax_pct) > 0 ? ` + ${quote.tax_pct}% tax` : ''}`
                    : monthlyTotal > 0 || visitTotal > 0
                    ? [monthlyTotal > 0 ? `${fmtMoney(monthlyTotal)}/mo` : null, visitTotal > 0 ? `${fmtMoney(visitTotal)}/visit` : null, oneTimeTotal > 0 ? `${fmtMoney(oneTimeTotal)} one-time` : null].filter(Boolean).join(' + ')
                    : fmtMoney(displayTotal)}
                </span>
              </div>
              {lineItems.some((li: any) => li.is_optional) && (
                <p className="text-xs text-gray-500 mt-1">Includes selected optional items and option group selections.</p>
              )}
            </div>

            {/* Forward links (set after conversion) */}
            {(quote?.converted_to_job_id || quote?.converted_to_project_id) && (
              <div className="border-t border-gray-100 pt-3 space-y-1">
                {quote.converted_to_job_id && (
                  <p className="text-xs text-teal-700 font-medium">
                    → Converted to <Link to={`/jobs/${quote.converted_to_job_id}`} className="underline font-semibold">Job #{quote.converted_to_job_id}</Link>
                  </p>
                )}
                {quote.converted_to_project_id && (
                  // Owner's list, Oct 2026: "Vicki said converted to project 4 but no project 4 anywhere" — the number opens it.
                  <p className="text-xs text-teal-700 font-medium">
                    → Converted to <Link to={`/projects?open=${quote.converted_to_project_id}`} className="underline font-semibold">Project #{quote.converted_to_project_id}</Link>
                  </p>
                )}
              </div>
            )}

            {/* Notes: what the crew needs ("front only"), and what stays in the office */}
            <BidNotes key={quote?.id} quote={quote} canEdit={canEdit} onRefresh={onRefresh} />
          </div>

          {/* Footer */}
          <div className="px-5 py-4 border-t border-gray-200 flex-shrink-0">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex gap-2 flex-wrap">
                {canSend && (
                  <button onClick={() => setShowSend(true)}
                    className="btn-secondary text-sm flex items-center gap-1.5">
                    <Share2 className="h-3.5 w-3.5" /> Share / Send
                  </button>
                )}
                {quote?.id && (
                  <button onClick={copyLink} className="btn-secondary text-sm flex items-center gap-1.5" title="Copy the customer's link">
                    {linkCopied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />} {linkCopied ? 'Copied' : 'Copy link'}
                  </button>
                )}
                {quote?.id && canEdit && (
                  <button onClick={() => refreshCopy.mutate()} disabled={refreshCopy.isLoading} className="btn-secondary text-sm flex items-center gap-1.5" title="Rewrite the intro, the mowing line and the agreement from this bid's numbers">
                    <RefreshCw className={`h-3.5 w-3.5 ${refreshCopy.isLoading ? 'animate-spin' : ''}`} /> {refreshCopy.isLoading ? 'Refreshing…' : 'Refresh wording'}
                  </button>
                )}
                {unsigned && (
                  <span className="text-sm font-semibold text-amber-900 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2">
                    ✍️ Not signed yet — nothing is accepted until they sign. {quote?.status === 'draft' ? 'Send them the bid.' : 'Send them the link again if they lost it.'}
                  </span>
                )}
                {quote?.status === 'sent' && !unsigned && (
                  <button onClick={() => onStatusChange('accepted')} className="btn-primary text-sm">
                    <Check className="h-3.5 w-3.5 mr-1" /> Mark Accepted
                  </button>
                )}
                {(quote?.status === 'sent' || quote?.status === 'accepted') && (
                  <button onClick={() => onStatusChange('declined')}
                    className="text-sm text-red-600 border border-red-200 rounded-lg px-3 py-2 hover:bg-red-50">
                    Mark Declined
                  </button>
                )}
                {/* Accepting a weekly / every-two-weeks bid already put the yard on the route (owner, Sep 19 2026: "why does
                    the bid say put on mowing schedule if automatically done"). This button used to show anyway, and it
                    makes a separate one-off job — a second stop for the same yard. Now: on the route → it says so; a
                    repeating bid not yet on the route → the real scheduler.
                    Oct 6 2026: an every-4-weeks bid is a mowing series and a one-time mow is booked by the accept itself,
                    so both go through the same button — "make the job" for them made the same mow a second time. Only a
                    mowing bid with no frequency at all still makes a job by hand. */}
                {canConvert && bidKind === 'mowing' && schedule && (
                  <span className="text-sm font-semibold text-emerald-800 px-1 py-2">✓ On the mowing schedule — {schedule.words || `${schedule.day}s`}</span>
                )}
                {canConvert && bidKind === 'mowing' && !schedule && oneTimeMow && (
                  <span className="text-sm font-semibold text-emerald-800 px-1 py-2">✓ One mow booked — {oneTimeMow.date_words}</span>
                )}
                {canConvert && !unsigned && bidKind === 'mowing' && !schedule && !oneTimeMow && !noMowLine && ['weekly', 'biweekly', 'monthly', 'one_time'].includes(String(quote?.service_frequency || '')) && (
                  <button onClick={() => (quote?.status === 'accepted' ? putOnSchedule.mutate() : onStatusChange('accepted'))} disabled={putOnSchedule.isLoading} className="btn-primary text-sm disabled:opacity-50">
                    {putOnSchedule.isLoading ? 'Scheduling…' : quote?.service_frequency === 'one_time'
                      ? (quote?.status === 'accepted' ? 'Book the mow' : 'Accept & book the mow')
                      : (quote?.status === 'accepted' ? 'Put on the mowing schedule' : 'Accept & put on the mowing schedule')}
                  </button>
                )}
                {canConvert && !unsigned && bidKind === 'mowing' && !schedule && !oneTimeMow && (noMowLine || !['weekly', 'biweekly', 'monthly', 'one_time'].includes(String(quote?.service_frequency || ''))) && (
                  <button onClick={() => setShowConvert(true)} className="btn-primary text-sm">
                    Make the mowing job (pick the day)
                  </button>
                )}
                {/* Project work is a job that is not a mow (owner, Sep 14 2026), so this opens the job form: one step puts
                    it on a day, on the calendar and on the crew's phones (owner, Oct 8 2026: "I don't want two steps; if I
                    turn it into project work that is enough"). It used to make a row on the Projects page that no
                    calendar read, and then the bid page asked for the job as well. On an accepted bid the green box
                    above already has the button. */}
                {canConvert && !unsigned && bidKind !== 'mowing' && quote?.status !== 'accepted' && (
                  <button onClick={() => setShowConvert(true)} className="btn-primary text-sm">
                    Convert to Project Work
                  </button>
                )}
                {quote?.status === 'accepted' && (
                  <button onClick={() => onStatusChange('completed')}
                    className="text-sm text-brand-700 border border-brand-200 rounded-lg px-3 py-2 hover:bg-brand-50">
                    Mark Complete
                  </button>
                )}
                {/* The one way to change a bid they already accepted. */}
                {canChangeOrder && (
                  <button onClick={() => setShowChangeOrder(true)}
                    className={`text-left text-sm rounded-lg px-3 py-2 border ${openOrder ? 'border-amber-300 bg-amber-50 text-amber-900' : 'border-brand-300 bg-brand-50 text-brand-900'}`}>
                    <span className="block font-semibold">{changeOrderLabel}</span>
                    {!openOrder && <span className="block text-[11px] text-brand-800">Change this bid after they accepted it, then send it to them.</span>}
                  </button>
                )}
                {quote?.status === 'completed' && (
                  <span className="text-sm text-brand-700 font-medium px-2 py-1">✓ Completed</span>
                )}
                {quote?.status === 'converted' && (
                  <span className="text-sm text-teal-700 font-medium px-2 py-1">✓ Converted</span>
                )}
              </div>
              <div className="flex gap-2">
                {quote?.status !== 'completed' && quote?.status !== 'declined' && (
                  <button onClick={() => { if (window.confirm('Delete this quote?')) onDelete(); }}
                    className="text-sm text-red-600 hover:text-red-700 px-2 py-1 flex items-center gap-1">
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </button>
                )}
                <button onClick={onClose} className="btn-secondary text-sm">Close</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showSend && quote && (
        <SendQuoteModal
          quote={quote}
          smsConfigured={smsConfigured ?? false}
          onClose={() => setShowSend(false)}
          onMarkSent={() => { onStatusChange('sent'); setShowSend(false); }}
        />
      )}

      {showConvert && quote && (
        <ConvertToJobModal
          quote={quote}
          routes={routes}
          crews={crews}
          members={members}
          project={convertedProject && !convertedProject.gone && !convertedProject.own && !convertedProject.job_id ? convertedProject : null}
          onClose={() => setShowConvert(false)}
          onSuccess={() => { setShowConvert(false); onRefresh(); qc.invalidateQueries('quotes'); }}
        />
      )}

      {showChangeOrder && quote && (
        <ChangeOrderEditor
          quote={quote}
          earlierApplied={(changeOrders?.history || []).some((c: any) => c.status === 'applied')}
          onClose={() => setShowChangeOrder(false)}
          onChanged={afterChangeOrder}
        />
      )}

    </>
  );
}

// ── Send / Share modal ────────────────────────────────────────────────────────
function SendQuoteModal({ quote, onClose, onMarkSent, smsConfigured }: {
  quote: any; onClose: () => void; onMarkSent: () => void; smsConfigured?: boolean;
}) {
  const [token, setToken] = useState<string>(quote.share_token || '');
  const [copied, setCopied] = useState(false);
  const [seeing, setSeeing] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailMsg, setEmailMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [smsSending, setSmsSending] = useState(false);
  const [smsMsg, setSmsMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  async function sendViaSms() {
    setSmsSending(true);
    setSmsMsg(null);
    try {
      const res = await api.post(`/quotes/${quote.id}/send-sms`);
      setSmsMsg({ type: 'success', text: res.data.message || 'Quote SMS sent!' });
    } catch (err: any) {
      setSmsMsg({ type: 'error', text: err.response?.data?.message || err.message || 'Failed to send SMS.' });
    } finally {
      setSmsSending(false);
    }
  }

  // null until the tick list loads; '' when every address is unticked.
  const [emailPick, setEmailPick] = useState<string | null>(null);
  // Nobody on file: the picker shows a box to type one, and the send saves it to their card.
  const [emailMissing, setEmailMissing] = useState(false);
  const [pickerKey, setPickerKey] = useState(0);
  async function sendViaEmail() {
    setEmailSending(true);
    setEmailMsg(null);
    try {
      if (emailMissing && !looksLikeEmail(emailPick || '')) {
        setEmailMsg({ type: 'error', text: `${quote.customer_name || 'They'} ha${quote.customer_name ? 's' : 've'} no email on file — type it in the box below, then tap Send via Email. It is saved to their client card.` });
        return;
      }
      if (emailPick === '') { setEmailMsg({ type: 'error', text: 'Tick at least one email.' }); return; }
      const res = await api.post(`/quotes/${quote.id}/send-email`, emailPick ? { to: emailPick, ...(emailMissing ? { save_email: true } : {}) } : {});
      setEmailMsg({ type: 'success', text: res.data.message || 'Quote emailed!' });
      // Their card has it now: the box turns back into the usual list.
      if (emailMissing) setPickerKey(k => k + 1);
    } catch (err: any) {
      setEmailMsg({ type: 'error', text: err.response?.data?.message || err.message || 'Failed to send email.' });
    } finally {
      setEmailSending(false);
    }
  }

  const getToken = useMutation(async () => {
    const r = await api.post(`/quotes/${quote.id}/share-token`);
    return r.data.token as string;
  }, {
    onSuccess: (t) => { setToken(t); },
    onError: () => { toast.error('Failed to generate link'); },
  });

  React.useEffect(() => {
    if (!token) getToken.mutate();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const shareUrl = token ? `${window.location.origin}/q/${token}` : '';
  const message = token
    ? `Hi ${quote.customer_name || 'there'}, your quote for "${quote.title}" is ready! Please review and accept here:\n${shareUrl}`
    : '';

  function copyUrl() {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function copyMessage() {
    if (!message) return;
    navigator.clipboard.writeText(message);
    toast.success('Message copied');
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="text-base font-black uppercase tracking-tight text-gray-900">Share Quote</h3>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-500 hover:text-gray-600" /></button>
        </div>
        <div className="px-5 py-5 space-y-4">
          {getToken.isLoading ? (
            <p className="text-sm text-gray-500 text-center py-4">Generating link…</p>
          ) : token ? (
            <>
              <div>
                {seeing && shareUrl && (
                  <CustomerView url={shareUrl} title={`What ${quote.customer_name || 'they'} see`} onClose={() => setSeeing(false)} />
                )}
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Quote Link</label>
                <div className="flex gap-2">
                  <input readOnly value={shareUrl}
                    className="input text-sm flex-1 bg-gray-50 text-gray-700"
                    onClick={e => (e.target as HTMLInputElement).select()} />
                  <button onClick={copyUrl}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-colors flex-shrink-0 ${copied ? 'bg-green-50 border-green-300 text-green-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                    {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                  {/* See what they see without leaving the app (owner, Sep 21 2026). */}
                  <button onClick={() => setSeeing(true)} disabled={!shareUrl}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 flex-shrink-0 disabled:opacity-50">
                    <Eye className="h-3.5 w-3.5" /> Preview
                  </button>
                  <a href={officeLink(shareUrl)} target="_blank" rel="noreferrer"
                    className="flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 flex-shrink-0">
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Message Template</label>
                <textarea readOnly value={message} className="input text-sm w-full bg-gray-50 text-gray-700 resize-none" rows={4} />
                <button onClick={copyMessage}
                  className="mt-2 flex items-center gap-1.5 text-xs text-brand-600 font-medium hover:text-brand-700">
                  <Copy className="h-3.5 w-3.5" /> Copy message
                </button>
              </div>
              <p className="text-xs text-gray-500">
                Send this link to <strong>{quote.customer_name}</strong> via text or email. They can view, adjust optional items, and accept or decline directly from the link.
              </p>
            </>
          ) : (
            <p className="text-sm text-red-500 text-center py-4">Failed to generate link.</p>
          )}
        </div>
        {emailMsg && (
          <div className={`mx-5 mb-0 rounded-md px-3 py-2 text-sm ${emailMsg.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-700'}`}>
            {emailMsg.text}
          </div>
        )}
        {smsMsg && (
          <div className={`mx-5 mb-0 rounded-md px-3 py-2 text-sm ${smsMsg.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-700'}`}>
            {smsMsg.text}
          </div>
        )}
        {token && <div className="px-5 pt-3"><EmailPicker key={pickerKey} url={`/quotes/${quote.id}/email-to`} onChange={setEmailPick} onMissing={setEmailMissing} who={quote.customer_name} /></div>}
        <div className="px-5 py-4 border-t border-gray-200 flex items-center gap-2 flex-wrap">
          {token && (
            <button
              onClick={sendViaEmail}
              disabled={emailSending}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-brand-300 rounded-lg text-brand-700 bg-brand-50 hover:bg-brand-100 disabled:opacity-50"
            >
              <Mail className="h-3.5 w-3.5" />
              {emailSending ? 'Sending…' : 'Send via Email'}
            </button>
          )}
          {token && smsConfigured && (
            <button
              onClick={sendViaSms}
              disabled={smsSending}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-green-300 rounded-lg text-green-700 bg-green-50 hover:bg-green-100 disabled:opacity-50"
            >
              <MessageSquare className="h-3.5 w-3.5" />
              {smsSending ? 'Sending…' : 'Send via SMS'}
            </button>
          )}
          {quote.status === 'draft' && token && (
            <button onClick={onMarkSent} className="btn-primary text-sm">
              Mark as Sent
            </button>
          )}
          {quote.status === 'sent' && (
            <span className="text-xs text-blue-600 font-medium">Quote is marked as sent</span>
          )}
          <button onClick={onClose} className="btn-secondary text-sm ml-auto">Done</button>
        </div>
      </div>
    </div>
  );
}

// ── Convert to Job modal ──────────────────────────────────────────────────────
function ConvertToJobModal({ quote, routes, crews, members, project = null, onClose, onSuccess }: {
  quote: any; routes: any[]; crews: any[]; members: any[]; project?: any;
  onClose: () => void; onSuccess: () => void;
}) {
  const today = businessToday();
  // The bid's start date, when it has one still ahead, is the day the job is made for; else the day he gave the
  // project this job takes the place of.
  const bidStart = String(quote?.start_date || '').slice(0, 10);
  const projectDay = String(project?.date_start || '').slice(0, 10);
  // Only a mowing bid makes a mowing job. This form used to start on "Mowing" for every bid, so a stump grinding
  // made here was a mow: "Mowing" on the crew's phone, counted toward the mowing credit, missing from Projects.
  const mowBid = !!(quote?.service_frequency || quote?.package_key || /\bmow/i.test(String(quote?.title || '')));
  const [form, setForm] = useState({
    scheduled_date: bidStart && bidStart >= today ? bidStart : projectDay && projectDay >= today ? projectDay : today,
    time_start: String(quote?.start_time || project?.time_start || '').slice(0, 5),
    service_type: mowBid ? 'mowing' : '',
    route_id: '',
    crew_id: '',
    assigned_crew_member_id: '',
    is_open_shift: false,
    open_shift_required_count: 1,
  });

  const filteredMembers = form.crew_id
    ? members.filter((m: any) => String(m.crew_id) === form.crew_id)
    : members;

  const convertToJob = useMutation(async () => {
    const r = await api.post(`/quotes/${quote.id}/convert-to-job`, {
      scheduled_date: form.scheduled_date,
      time_start: form.time_start || null,
      service_type: form.service_type || undefined,   // blank: the server reads the kind of work off the bid
      route_id: form.route_id ? Number(form.route_id) : null,
      crew_id: form.crew_id ? Number(form.crew_id) : null,
      assigned_crew_member_id: form.assigned_crew_member_id ? Number(form.assigned_crew_member_id) : null,
      is_open_shift: form.is_open_shift ? 1 : 0,
      open_shift_required_count: form.open_shift_required_count,
    });
    return r.data;
  }, {
    onSuccess: (data) => {
      const day = /^\d{4}-\d{2}-\d{2}/.test(String(data.scheduled_date || ''))
        ? new Date(`${String(data.scheduled_date).slice(0, 10)}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
        : data.scheduled_date;
      toast.success(data.replaced_project_id
        ? `Job scheduled for ${day} — it takes the place of project #${data.replaced_project_id}`
        : `Job scheduled for ${day}`);
      onSuccess();
    },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Failed to convert'); },
  });

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="text-base font-black uppercase tracking-tight text-gray-900">{mowBid ? 'Convert to Job' : 'Project work — pick the day'}</h3>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-500 hover:text-gray-600" /></button>
        </div>
        <div className="px-5 py-5 space-y-4">
          <div className="bg-brand-50 border border-brand-200 rounded-lg px-3 py-2 text-sm text-brand-800">
            <strong>{quote.title}</strong> · {quote.customer_name} · {fmtMoney(quote.total)}
          </div>
          {project && (
            <p className="text-xs text-gray-600">This takes the place of project #{project.id}, so the work is on the calendar once and billed once. Its day and anything typed on it come along.</p>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date *">
              <input type="date" value={form.scheduled_date}
                onChange={e => setForm(f => ({ ...f, scheduled_date: e.target.value }))} className="input" />
            </Field>
            <Field label="Start time">
              <input type="time" value={form.time_start}
                onChange={e => setForm(f => ({ ...f, time_start: e.target.value }))} className="input" />
            </Field>
          </div>
          <Field label="Service type">
            <select value={form.service_type} onChange={e => setForm(f => ({ ...f, service_type: e.target.value }))} className="input">
              {!mowBid && <option value="">From the bid</option>}
              {SERVICE_TYPES.map(st => <option key={st.value} value={st.value}>{st.label}</option>)}
            </select>
          </Field>
          {routes.length > 0 && (
            <Field label="Route (optional)">
              <select value={form.route_id} onChange={e => setForm(f => ({ ...f, route_id: e.target.value }))} className="input">
                <option value="">— No route —</option>
                {routes.filter((r: any) => r.is_active !== 0).map((r: any) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </Field>
          )}
          {members.length > 0 && (
            <Field label="Assigned member (optional)">
              <select value={form.assigned_crew_member_id}
                onChange={e => setForm(f => ({ ...f, assigned_crew_member_id: e.target.value }))} className="input">
                <option value="">— Unassigned —</option>
                {filteredMembers.map((m: any) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </Field>
          )}
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={form.is_open_shift}
              onChange={e => setForm(f => ({ ...f, is_open_shift: e.target.checked }))}
              className="rounded text-brand-600" />
            Open shift (crew members can claim)
          </label>
          {form.is_open_shift && (
            <Field label="Required headcount">
              <input type="number" min={1} max={20} value={form.open_shift_required_count}
                onChange={e => setForm(f => ({ ...f, open_shift_required_count: Number(e.target.value) }))}
                className="input w-24" />
            </Field>
          )}
        </div>
        <div className="px-5 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={() => convertToJob.mutate()} disabled={convertToJob.isLoading || !form.scheduled_date}
            className="btn-primary">
            {convertToJob.isLoading ? 'Creating…' : mowBid ? 'Create Job' : 'Put it on the calendar'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-base font-black uppercase tracking-tight text-gray-900">{title}</h3>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-500 hover:text-gray-600" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-3 last:mb-0">
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
    </div>
  );
}


// ── Route day picker ──────────────────────────────────────────────────────────
// For a NEW yard: the recommended day (service-area rules first, distance
// second), ranked alternatives, and an override. An existing mow client's day
// is not the bid's to change — that happens in Routes.
// ── Schedule on the bid: when it starts, how often, which day ────────────────
// Start day and route day can differ: the first visit is the start day, then
// the yard repeats on the route day (picked below) at this frequency.
/** Who sent them. Captured on the bid so it is there when they accept. */
function ReferralRow({ quote, canEdit, onRefresh }: { quote: any; canEdit: boolean; onRefresh: () => void }) {
  const qc = useQueryClient();
  const save = useMutation((body: any) => api.put(`/quotes/${quote.id}`, body), {
    onSuccess: () => { toast.success('Referral code saved on the bid'); onRefresh(); qc.invalidateQueries('quotes'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not save that'); },
  });
  const cur = String(quote?.referral_code || '');
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Referral code</p>
      {canEdit ? (
        <input className="input text-sm py-1 w-48" key={cur} defaultValue={cur} placeholder="If a neighbor sent them" autoCapitalize="characters"
          onBlur={e => { const v = e.target.value.trim().toUpperCase(); if (v !== cur) save.mutate({ referral_code: v || null }); }} />
      ) : (
        <p className="text-sm text-gray-800">{cur || <span className="text-gray-500">None</span>}</p>
      )}
    </div>
  );
}

/**
 * The first visit on the mowing card, editable right there (owner, Oct 6 2026:
 * "allow me to adjust start date on bid even after it is sent"). Open while
 * the bid is a draft, sent, or accepted but not yet on the schedule — once
 * the schedule exists, the date moves in Routes.
 */
function FirstVisitEdit({ quote, canEdit, onRefresh, word = 'First visit' }: { quote: any; canEdit: boolean; onRefresh: () => void; word?: string }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const start = String(quote?.start_date || '').slice(0, 10);
  const save = useMutation((body: any) => api.put(`/quotes/${quote.id}`, body), {
    onSuccess: (r: any) => { if (r.status !== 202) toast.success(`${word} saved on the bid`); onRefresh(); qc.invalidateQueries('quotes'); qc.invalidateQueries(['quote-day-options', quote?.id]); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not save that'); },
  });
  const time = quote?.start_time ? (() => { const [h, m] = String(quote.start_time).split(':').map(Number); return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'am' : 'pm'}`; })() : '';
  const label = start ? `${word} ${new Date(`${start}T12:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}${time ? ` at ${time}` : ' — no time set'}` : `No ${word.toLowerCase()}`;
  if (!canEdit) return start ? <p className="text-xs text-gray-700">{label}</p> : null;
  if (!open) {
    return (
      <p className="text-xs text-gray-700">{label} <button type="button" onClick={() => setOpen(true)} className="text-brand-700 font-semibold underline ml-1">{start ? 'Change' : 'Set date'}</button></p>
    );
  }
  return (
    <div className="flex flex-wrap items-end gap-2 text-xs text-gray-700">
      <label>{word}
        <input type="date" className="input text-sm py-1 block mt-0.5" defaultValue={start} key={start}
          onChange={e => { const v = e.target.value; if (v && v !== start) save.mutate({ start_date: v }); }} />
      </label>
      <label>Time
        <input type="time" step={900} className="input text-sm py-1 block mt-0.5" defaultValue={String(quote?.start_time || '')} key={`t-${quote?.start_time || ''}`}
          onChange={e => { const v = e.target.value; if (v !== String(quote?.start_time || '')) save.mutate({ start_time: v || null }); }} />
      </label>
      <button type="button" onClick={() => setOpen(false)} className="text-brand-700 font-semibold underline pb-1.5">Done</button>
      {quote?.status === 'sent' && <p className="w-full text-[11px] text-gray-500">The bid link shows the new date as soon as it is saved. Text them if they already read it.</p>}
    </div>
  );
}

function ScheduleRow({ quote, canEdit, onRefresh }: { quote: any; canEdit: boolean; onRefresh: () => void }) {
  const qc = useQueryClient();
  const names = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const save = useMutation((body: any) => api.put(`/quotes/${quote.id}`, body), {
    onSuccess: (r: any) => { if (r.status !== 202) toast.success('Schedule saved on the bid'); onRefresh(); qc.invalidateQueries('quotes'); qc.invalidateQueries(['quote-day-options', quote?.id]); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not save that'); },
  });
  const start = String(quote?.start_date || '').slice(0, 10);
  const freq = quote?.service_frequency || '';
  const FREQ: Record<string, string> = { weekly: 'Weekly', biweekly: 'Every 2 weeks', monthly: 'Every 4 weeks', one_time: 'One time' };
  // What accepting this bid books, in the server's words — the same dates the customer's page shows
  // (GET /quotes/:id/day-options → start; owner, Oct 5 2026: "start days … based on new route days"). This row used
  // to say "then every Tuesday" for an every-2-weeks bid and knew nothing about which week the route is out.
  const { data: dayData } = useQuery(['quote-day-options', quote?.id], () => api.get(`/quotes/${quote.id}/day-options`).then(r => r.data), { enabled: !!quote?.property_id });
  const startPlan: any = dayData?.start || null;
  const startOpts: any[] = startPlan?.options || [];
  const fmt = (d: string) => d ? new Date(d + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '';
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Schedule</p>
      {canEdit ? (
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs text-gray-600">First visit
            <input type="date" className="input text-sm py-1 block mt-0.5" defaultValue={start} key={start}
              onChange={e => { const v = e.target.value; if (v && v !== start) save.mutate({ start_date: v }); }} />
            <div className="flex items-center gap-2 mt-1">
              <input type="time" step={900} className="input text-sm py-1 block" defaultValue={String(quote?.start_time || '')} key={`t-${quote?.start_time || ''}`} title="The first mow's time"
                onChange={e => { const v = e.target.value; if (v !== String(quote?.start_time || '')) save.mutate({ start_time: v || null }); }} />
              {quote?.start_time && canEdit && <button type="button" onClick={() => save.mutate({ start_time: null })} className="text-xs text-gray-600 underline" title="No set time — anytime that day">Clear</button>}
            </div>
          </label>
          <label className="text-xs text-gray-600">How often
            <select className="input text-sm py-1 block mt-0.5" value={freq} onChange={e => save.mutate({ service_frequency: e.target.value || null })} disabled={save.isLoading}>
              <option value="">— pick —</option>
              {Object.entries(FREQ).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
          {freq === 'weekly' && (
            <label className="text-xs text-gray-600">Weekly discount %
              <input type="number" min="0" max="100" inputMode="decimal" className="input text-sm py-1 block mt-0.5 w-24" key={String(quote?.weekly_discount_pct ?? '')}
                defaultValue={quote?.weekly_discount_pct ?? ''} placeholder="default"
                onBlur={e => { const v = e.target.value; if (v !== String(quote?.weekly_discount_pct ?? '')) save.mutate({ weekly_discount_pct: v === '' ? null : Number(v) }); }} />
            </label>
          )}
          <label className="text-xs text-gray-600">Per mow before discount $
            <input type="number" min="0" inputMode="decimal" className="input text-sm py-1 block mt-0.5 w-24" key={String(quote?.mow_price ?? '')}
              defaultValue={quote?.mow_price ?? ''} placeholder="—"
              onBlur={e => { const v = e.target.value; if (v !== String(quote?.mow_price ?? '')) save.mutate({ mow_price: v === '' ? null : Number(v) }); }} />
          </label>
          <p className="text-xs text-gray-500 pb-1.5">
            {startOpts.length ? null : dayData?.cadence_words ? <>then <b>{dayData.cadence_words}</b></> : freq === 'one_time' ? 'one mow' : quote?.service_day != null ? <>on <b>{names[quote.service_day]}s</b></> : 'route day: pick one below'}
          </p>
          {start && <button type="button" onClick={() => save.mutate({ start_date: null })} className="text-[11px] text-gray-500 underline pb-1.5">clear start</button>}
        </div>
      ) : (
        <p className="text-sm text-gray-700">{start ? `First visit ${fmt(start)}` : 'No first visit agreed'}{freq ? ` · ${FREQ[freq] || freq}` : ''}{dayData?.cadence_words ? ` · then ${dayData.cadence_words}` : ''}</p>
      )}
      {/* What accept books — one line per date the customer may pick; amber when no crew is out on the first mow's day. */}
      {canEdit && startPlan?.none && <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 mt-2">Accepting will not put this yard on a route yet: {startPlan.message}</p>}
      {canEdit && startOpts.map((o: any) => (
        <div key={o.key} className="mt-1.5">
          <p className="text-xs text-gray-700">{startOpts.length > 1 ? <b>{o.label}{o.key === startPlan.default ? ' (booked unless they pick the other)' : ''}: </b> : <b>Accepting books: </b>}{o.summary}</p>
          {o.warning && <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 mt-1">{o.warning}</p>}
        </div>
      ))}
    </div>
  );
}

function RouteDayPicker({ quote, canEdit, onRefresh }: { quote: any; canEdit: boolean; onRefresh: () => void }) {
  const qc = useQueryClient();
  const { data, refetch } = useQuery(['quote-day-options', quote?.id], () => api.get(`/quotes/${quote.id}/day-options`).then(r => r.data), { enabled: !!quote?.property_id });
  const [override, setOverride] = useState(false);
  const [dow, setDow] = useState('');
  const [routeId, setRouteId] = useState('');
  const [firstTime, setFirstTime] = useState(false);
  const save = useMutation((body: any) => api.put(`/quotes/${quote.id}`, { ...body, first_time: firstTime || undefined }), {
    // When the day sent was not the route's own, the server put the route's day on the bid and says so.
    onSuccess: (r: any) => { if (r.status !== 202) { if (r.data?.day_note) toast.info(r.data.day_note); else toast.success('Day saved on the bid'); } onRefresh(); refetch(); qc.invalidateQueries('quotes'); setOverride(false); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not save the day'); },
  });
  if (!quote?.property_id) return <p className="text-xs text-gray-500 italic">Put a property on the bid to pick its route day.</p>;
  if (!data) return null;
  const names: string[] = data.day_names || ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const cur = data.current || {};
  const options: any[] = data.options || [];
  const blocked = !!data.existing && !firstTime;
  // "Pick a different day…": only the routes this yard may ride, each under its OWN weekday (owner, Oct 5 2026:
  // "new route days based on new route days"). A route used to show under every day when its card had no weekday.
  const routes: any[] = data.routes || [];
  const routesOn = (d: string) => routes.filter((r: any) => d !== '' && String(r.day_of_week) === d);
  const routeDays: number[] = Array.from(new Set<number>(routes.map((r: any) => Number(r.day_of_week)))).sort((a, b) => a - b);
  const dayChoices: number[] = routeDays.length ? routeDays : names.map((_, i) => i);
  // The line for a route already on the bid — for a weekly yard that is the line holding either of its two routes.
  const lineFor = (d: string, id: any) => routesOn(d).find((r: any) => r.id === id || r.other_week_route_id === id) || routesOn(d)[0];
  const pickDay = (d: string, id?: any) => { setDow(d); const r = lineFor(d, id); setRouteId(r ? String(r.id) : ''); };
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Route day{data.area_label ? <span className="normal-case font-normal text-gray-500"> · {data.area_label}</span> : null}</p>
        {cur.service_day != null && <span className="text-xs font-semibold text-emerald-700">On the bid: {names[cur.service_day]}{cur.route_words || cur.route_name ? ` · ${cur.route_words || cur.route_name}` : ''}{cur.next_run_words ? ` · next out ${cur.next_run_words}` : ''}</span>}
      </div>
      {data.existing && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 mb-2">
          Already mowed on <b>{data.existing.day}s</b>{data.existing.route_words || data.existing.route_name ? ` (${data.existing.route_words || data.existing.route_name})` : ''}. Their day changes in Routes, not on a bid.
          {canEdit && <label className="flex items-center gap-2 mt-1 text-amber-900"><input type="checkbox" checked={firstTime} onChange={e => setFirstTime(e.target.checked)} className="rounded" /> This is a first-time job for this yard anyway</label>}
        </div>
      )}
      {canEdit && !blocked && (
        <div className="space-y-1.5">
          {options.length === 0 && <p className="text-xs text-gray-500 italic">{data.message || 'No recommendation yet — pin the address on the map, or pick a day below.'}</p>}
          {data.pin_problem && <p className="text-[11px] text-amber-700">{data.pin_problem}</p>}
          {data.method === 'detour' && options.length > 0 && <p className="text-[11px] text-gray-500">Ranked by the driving each route would add, as the routes run today.</p>}
          {data.method === 'town' && options.length > 0 && <p className="text-[11px] text-amber-700">This address has no map pin yet, so these are the days {data.area_label || 'this town'} is mowed now — not the drive.</p>}
          {options.map((o: any, i: number) => {
            // A weekly yard rides both routes of its day, so either of the two on the bid is this line.
            const isCur = cur.service_day === o.day_of_week && ((cur.route_id || null) === (o.route_id || null) || (!!o.other_week_route_id && cur.route_id === o.other_week_route_id));
            return (
              <button key={`${o.day_of_week}-${o.route_id || 'x'}`} type="button" onClick={() => save.mutate({ service_day: o.day_of_week, route_id: o.route_id || null })} disabled={save.isLoading}
                className={`w-full text-left rounded-lg border px-3 py-2 ${isCur ? 'border-emerald-600 bg-emerald-50' : i === 0 ? 'border-emerald-300 bg-white' : 'border-gray-200 bg-white'} hover:border-emerald-500`}>
                <p className="text-sm font-semibold text-gray-900">{i === 0 ? '★ ' : `${i + 1}. `}{o.label || `${o.day}${o.route_name ? ` · ${o.route_name}` : ''}`}{isCur ? ' ✓' : ''}</p>
                <p className="text-[11px] text-gray-500">{o.reason}</p>
              </button>
            );
          })}
          {!override ? (
            <button type="button" onClick={() => { setOverride(true); pickDay(cur.service_day != null && dayChoices.includes(Number(cur.service_day)) ? String(cur.service_day) : '', cur.route_id); }} className="text-xs text-brand-600 hover:text-brand-800 font-medium">Pick a different day…</button>
          ) : (
            <div className="rounded-lg bg-gray-50 p-2 space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <select value={dow} onChange={e => pickDay(e.target.value)} className="input text-sm py-1">
                  <option value="">— Day —</option>
                  {dayChoices.map(i => <option key={i} value={String(i)}>{names[i]}</option>)}
                </select>
                <select value={routeId} onChange={e => setRouteId(e.target.value)} className="input text-sm py-1" disabled={dow === ''}>
                  {routesOn(dow).length === 0 && <option value="">No route yet</option>}
                  {routesOn(dow).map((r: any) => <option key={r.id} value={String(r.id)}>{r.name}</option>)}
                </select>
                <button type="button" onClick={() => save.mutate({ service_day: dow === '' ? null : Number(dow), route_id: routeId ? Number(routeId) : null })} disabled={save.isLoading || dow === ''} className="btn-primary text-xs">Save</button>
                <button type="button" onClick={() => setOverride(false)} className="btn-secondary text-xs">Cancel</button>
              </div>
              {routes.length > 0 && <p className="text-[11px] text-gray-500">Only the days a route is running are listed, each route under its own day.</p>}
            </div>
          )}
        </div>
      )}
      {!canEdit && cur.service_day == null && <p className="text-xs text-gray-500 italic">No day picked.</p>}
    </div>
  );
}

// ── Notes on the bid ──────────────────────────────────────────────────────────
// Crew notes travel to the property when the yard is scheduled ("front only");
// internal notes stay in the office. Both editable any time the bid is open.
function BidNotes({ quote, canEdit, onRefresh }: { quote: any; canEdit: boolean; onRefresh: () => void }) {
  const qc = useQueryClient();
  const [crew, setCrew] = useState<string>(quote?.crew_notes || '');
  const [internal, setInternal] = useState<string>(quote?.notes || '');
  const dirty = crew !== (quote?.crew_notes || '') || internal !== (quote?.notes || '');
  const save = useMutation(() => api.put(`/quotes/${quote.id}`, { crew_notes: crew, notes: internal }), {
    onSuccess: (r: any) => { if (r.status !== 202) toast.success('Notes saved'); onRefresh(); qc.invalidateQueries('quotes'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not save the notes'); },
  });
  if (!canEdit) {
    if (!quote?.crew_notes && !quote?.notes) return null;
    return (
      <div className="text-xs text-gray-500 border-t border-gray-100 pt-3 space-y-1">
        {quote.crew_notes && <p><span className="font-semibold text-gray-600">Crew:</span> {quote.crew_notes}</p>}
        {quote.notes && <p className="italic">Notes: {quote.notes}</p>}
      </div>
    );
  }
  return (
    <div className="border-t border-gray-100 pt-3 space-y-2">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Notes</p>
      <label className="block text-xs text-gray-600">For the crew (goes to the yard when it's scheduled)
        <textarea value={crew} onChange={e => setCrew(e.target.value)} rows={2} className="input text-sm w-full mt-1" placeholder="front only · gate code · dog in the back" />
      </label>
      <label className="block text-xs text-gray-600">Internal (office only)
        <textarea value={internal} onChange={e => setInternal(e.target.value)} rows={2} className="input text-sm w-full mt-1" placeholder="pricing context, what to confirm" />
      </label>
      {dirty && <button type="button" onClick={() => save.mutate()} disabled={save.isLoading} className="btn-primary text-xs">{save.isLoading ? 'Saving…' : 'Save notes'}</button>}
    </div>
  );
}

/**
 * The paragraph at the top of the customer's bid page. The app writes it from
 * the on-site notes and the numbers; the owner reads it on the bid and fixes
 * the words (Sep 24 2026: "allow me to adjust this on the bid"). Once edited by
 * hand the app leaves it alone — Refresh wording no longer rewrites it.
 */
function IntroEditor({ quote, canEdit, onRefresh }: { quote: any; canEdit: boolean; onRefresh: () => void }) {
  const readOnly = useReadOnly();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const save = useMutation((body: any) => api.put(`/quotes/${quote.id}`, body), {
    onSuccess: () => { toast.success('Intro saved'); setEditing(false); onRefresh(); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not save the intro'); },
  });
  const intro = String(quote?.intro_note || '');
  if (!intro && !canEdit) return null;
  if (!editing) {
    return (
      <div className="mb-3">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Intro (top of their page)</p>
          {canEdit && !readOnly && (
            <button onClick={() => { setText(intro); setEditing(true); }} className="text-xs text-brand-600 font-medium">{intro ? 'Edit' : 'Add'}</button>
          )}
        </div>
        {intro
          ? <p className="text-sm text-gray-600 italic mt-1 whitespace-pre-wrap">{intro}</p>
          : <p className="text-xs text-gray-400 mt-1">No intro — the page opens with the prices.</p>}
      </div>
    );
  }
  return (
    <div className="mb-3">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Intro (top of their page)</p>
      <textarea value={text} onChange={e => setText(e.target.value)} rows={5} className="input text-sm w-full" autoFocus />
      <p className="text-[11px] text-gray-500 mt-1">Write it the way you'd say it. Prices still come from the line items, so there is no need to repeat them here.{quote?.ai_scope_json ? ' This bid has a written job scope, which the customer sees in place of the intro.' : ''}</p>
      <div className="flex gap-2 mt-2">
        <button onClick={() => save.mutate({ intro_note: text.trim() })} disabled={save.isLoading} className="btn-primary text-xs py-1 px-3 disabled:opacity-50">{save.isLoading ? 'Saving…' : 'Save'}</button>
        <button onClick={() => setEditing(false)} disabled={save.isLoading} className="btn-secondary text-xs py-1 px-3">Cancel</button>
      </div>
    </div>
  );
}
